import React, { useState, useEffect, useRef, useCallback } from 'react';
import useElementHeight from '../hooks/useElementHeight';
import ReasonPromptModal from '../components/ReasonPromptModal';
import { toast } from 'sonner';
import { truncateName } from '../utils/previewTruncate';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { shouldRenderDesktop } from '../utils/deviceDetection';
import { useAuth } from '../contexts/AuthContext';
import { useWebNotification } from '../hooks/useWebNotification';
import axios from 'axios';
import MediaPicker from '../components/MediaPicker';
import MarkSolvedNoteModal from '../components/MarkSolvedNoteModal';
import SavedTextsPanel from '../components/SavedTextsPanel';
import SavedPhotosPanel from '../components/SavedPhotosPanel';
import ActionStickerBubble from '../components/ActionStickerBubble';
import ThemeToggleMenuItem from '../components/ThemeToggleMenuItem';
import ZoomableImage from '../components/ZoomableImage';
import ChatHeaderActions from '../components/ChatHeaderActions';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { idbSaveMessages, idbGetMessages, idbSaveRoom, idbGetRoom, idbDeleteMessage, idbMarkDeleted, idbCleanupOld } from '../utils/idbCache';
import VoiceMessage from '../components/VoiceMessage';
import { onVisibilityFetch } from '../utils/pollingKeepAlive';
import WhatsAppInput from '../components/WhatsAppInput';
import AudioCallScreen from '../components/AudioCallScreen';
import socket from '../utils/socket';
import { deltaSyncMessages } from '../utils/messageSync';
import { parseBankFromPaste } from '../utils/parseBankFromPaste';
import DraggableModal from '../components/DraggableModal';
import {
  connectSocket,
  disconnectSocket,
  joinRoom,
  leaveRoom,
  onNewMessage,
  offNewMessage,
  onMessageUpdated,
  offMessageUpdated,
  onMessageDeleted,
  offMessageDeleted,
  markMessagesRead,
  onMessagesRead,
  offMessagesRead,
  onMessagesDelivered,
  offMessagesDelivered,
  onUserOnline,
  offUserOnline,
  onUserOffline,
  offUserOffline
} from '../utils/socket';
import OnlineStatus from '../components/OnlineStatus';
import EditClientModal from '../components/EditClientModal';
import EditEntryModal from '../components/EditEntryModal';
import FlagIcons from '../components/FlagIcons';
import MBSetupBadge from '../components/MBSetupBadge';
import { useMBList } from '../hooks/useMBList';
import AssignMBQuickPicker from '../components/AssignMBQuickPicker';
import MultiSelectForwardModal from '../components/MultiSelectForwardModal';
import { 
  ArrowLeft,
  UserCircle,
  PaperPlaneRight,
  DotsThreeVertical,
  Prohibit,
  Trash,
  Copy,
  ShareFat,
  Smiley,
  Plus,
  ArrowBendUpLeft,
  X,
  Eye,
  FileText,
  CaretDown,
  Phone,
  Star,
  Check,
  Checks,
  Clock,
  WarningCircle,
  PushPin,
  Bell,
  BellSlash,
} from '@phosphor-icons/react';
import PinDurationPicker from '../components/PinDurationPicker';
import ViewOnceBubble from '../components/ViewOnceBubble';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExts = ['.mp4', '.mov', '.avi', '.webm', '.mkv'];
  const lower = url.split('?')[0].toLowerCase();
  return videoExts.some(ext => lower.endsWith(ext));
};

const buildMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}/api${url}`;
  return `${API}${url}`;
};

// Date separator helpers
const getDateLabel = (timestamp) => {
  const msgDate = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (msgDate.toDateString() === today.toDateString()) return 'Today';
  if (msgDate.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return msgDate.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' });
};

const shouldShowDateSeparator = (msgs, idx) => {
  if (idx === 0) return true;
  return new Date(msgs[idx - 1].timestamp).toDateString() !== new Date(msgs[idx].timestamp).toDateString();
};

// Session-53 hardening — treat data-URI SVGs as stickers too (fallback for
// old messages missing the is_sticker flag).
const isStickerLike = (m) => !!(m && (m.is_sticker || (typeof m.image_url === 'string' && m.image_url.startsWith('data:image/svg'))));

const getImageGroup = (msgs, idx) => {
  const msg = msgs[idx];
  if (!msg.image_url || msg.voice_url || msg.document_url || isVideoUrl(msg.image_url) || isStickerLike(msg)) return null;
  if (msg.caption || (msg.text && msg.text.trim() && msg.text !== '🖼️ Photo' && msg.text !== '🎥 Video')) return null;
  if (idx > 0 && msgs[idx - 1].image_url && !msgs[idx - 1].voice_url && !msgs[idx - 1].document_url && !isVideoUrl(msgs[idx - 1].image_url) && !isStickerLike(msgs[idx - 1]) && msgs[idx - 1].sender_id === msg.sender_id && !msgs[idx - 1].caption && (!msgs[idx - 1].text || !msgs[idx - 1].text.trim() || msgs[idx - 1].text === '🖼️ Photo' || msgs[idx - 1].text === '🎥 Video')) return null;
  const group = [msg];
  for (let i = idx + 1; i < msgs.length; i++) {
    if (group.length >= 10) break;
    const nextMsg = msgs[i];
    // Bug #1b fix (Session-53) — added `!isStickerLike(nextMsg)` so a sticker
    // sent right after a photo doesn't collapse into the photo grid.
    if (!nextMsg.image_url || nextMsg.voice_url || nextMsg.document_url || isVideoUrl(nextMsg.image_url) || isStickerLike(nextMsg) || nextMsg.sender_id !== msg.sender_id) break;
    const hasCaption = nextMsg.caption || (nextMsg.text && nextMsg.text.trim() && nextMsg.text !== '🖼️ Photo' && nextMsg.text !== '🎥 Video');
    if (!hasCaption) {
      group.push(nextMsg);
    } else {
      // Point 6 — a captioned photo is standalone; stop WITHOUT adding it.
      break;
    }
  }
  return group.length > 1 ? group : null;
};

const isPartOfImageGroup = (msgs, idx) => {
  const msg = msgs[idx];
  if (!msg.image_url || msg.voice_url || msg.document_url || isVideoUrl(msg.image_url) || isStickerLike(msg) || idx === 0) return false;
  // Point 6 — a captioned photo is always its own standalone message.
  if (msg.caption || (msg.text && msg.text.trim() && msg.text !== '🖼️ Photo' && msg.text !== '🎥 Video')) return false;
  let groupCount = 0;
  for (let i = idx - 1; i >= Math.max(0, idx - 10); i--) {
    const prevMsg = msgs[i];
    // Bug #1a fix (Session-53) — `!isStickerLike(prevMsg)` so a photo right
    // after a sticker isn't wrongly hidden as part of a sticker group.
    if (prevMsg.image_url && !prevMsg.voice_url && !prevMsg.document_url && !isVideoUrl(prevMsg.image_url) && !isStickerLike(prevMsg) && prevMsg.sender_id === msg.sender_id && !prevMsg.caption && (!prevMsg.text || !prevMsg.text.trim() || prevMsg.text === '🖼️ Photo' || prevMsg.text === '🎥 Video')) {
      groupCount++;
    } else break;
  }
  return groupCount > 0 && groupCount < 10;
};

const AdminChat = ({ embeddedRoomId, onBackToList }) => {
  const { roomId: paramRoomId } = useParams();
  const roomId = embeddedRoomId || paramRoomId;
  const MB_SETUPS = useMBList();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const viewOnly = searchParams.get('viewOnly') === 'true';

  // P5 — a DIRECT /admin-chat/:id navigation on desktop would replace the
  // dashboard split-view with a full-screen chat. Bounce to the dashboard with
  // the room pre-selected so it opens as the EMBEDDED chat (split view intact).
  // Only the routed instance redirects (embedded passes embeddedRoomId); mobile
  // keeps full-screen; view-only inquiry chats stay full-screen (embedded chat
  // has no view-only mode).
  const _p5Redirected = useRef(false);
  useEffect(() => {
    if (embeddedRoomId || !paramRoomId || viewOnly) return;
    if (_p5Redirected.current || !shouldRenderDesktop()) return;
    _p5Redirected.current = true;
    const st = location.state || {};
    navigate('/admin-dashboard', {
      replace: true,
      state: { selectedChatId: paramRoomId, activeTab: st.activeTab, chatTypeTab: st.chatTypeTab },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embeddedRoomId, paramRoomId, viewOnly]);
  const { showNotification, playNotificationSound, requestPermission } = useWebNotification();
  const showNotificationRef = useRef(showNotification);
  showNotificationRef.current = showNotification;
  
  const [messages, setMessages] = useState([]);
  // Keep a live ref of messages so reconnect delta-sync can read the current
  // thread without stale closures.
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  const [customer, setCustomer] = useState(null);
  const [deletedPreview, setDeletedPreview] = useState(null); // admin-only: original deleted-message peek
  const [loading, setLoading] = useState(false); // Changed to false for instant loading
  const [showMenu, setShowMenu] = useState(false);
  const [showMBMenu, setShowMBMenu] = useState(false);
  const [longPressMenu, setLongPressMenu] = useState(null);
  const [contextMoreOpen, setContextMoreOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [pastedFile, setPastedFile] = useState(null);
  const [editEntry, setEditEntry] = useState(null); // { type, entryId }
  // Session-42: Admin service-request Create/Cancel modals (mirrors handler flow)
  const [showSRCreateModal, setShowSRCreateModal] = useState(false);
  const [showSRCancelModal, setShowSRCancelModal] = useState(false);
  // Session-48j — Mark-as-Solved optional note dialog
  const [markSolvedOpen, setMarkSolvedOpen] = useState(false);
  const [markSolvedBusy, setMarkSolvedBusy] = useState(false);
  const [markSolvedReqId, setMarkSolvedReqId] = useState(null);
  const doMarkSolved = async (note) => {
    setMarkSolvedBusy(true);
    try {
      const res = await axios.post(`${API}/support/mark-solved`, { room_id: roomId, actor_id: user.id, note, request_id: markSolvedReqId });
      if (res.data && res.data.no_open_request) {
        toast.error(res.data.message || 'No pending request to approve.');
        setMarkSolvedOpen(false);
        return;
      }
      toast.success('✅ Marked as solved');
      setMarkSolvedOpen(false);
      // Session-49: refresh so the bot ack ("Marked as SOLVED by …") shows
      // instantly in the chat — mobile socket sometimes misses the emit.
      try { loadMessages && loadMessages(); } catch (_) {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark solved');
    } finally { setMarkSolvedBusy(false); }
  };
  const [srAmount, setSrAmount] = useState('');
  const [srReference, setSrReference] = useState('');
  const [srBusy, setSrBusy] = useState(false);
  // P23/P24 — combined create+approve fields
  const [srUtr, setSrUtr] = useState('');
  const [srReceivedIn, setSrReceivedIn] = useState('');
  const [srDesc, setSrDesc] = useState('');
  const [srBank, setSrBank] = useState({ account_holder_name: '', bank_account_number: '', ifsc_code: '', bank_name: '', upi_id: '' });
  const [srBankAccounts, setSrBankAccounts] = useState([]); // P24 multi-account picker
  const [srBankPickId, setSrBankPickId] = useState('');
  const [srCancelList, setSrCancelList] = useState([]);
  const [srReasonForId, setSrReasonForId] = useState(null);
  const [srReasonText, setSrReasonText] = useState('');

  // P24 — when the Create Request modal opens on a WITHDRAW chat, prefill the
  // customer's saved bank details (editable). If the client has MULTIPLE saved
  // accounts (field-history), expose a dropdown to pick between them.
  useEffect(() => {
    if (!showSRCreateModal || customer?.chat_type !== 'withdrawal' || !customer?.id) return;
    let cancelled = false;
    (async () => {
      let base = { account_holder_name: '', bank_account_number: '', ifsc_code: '', bank_name: '', upi_id: '' };
      try {
        const res = await axios.get(`${API}/client-bank-details/${customer.id}`);
        const d = res.data?.bank_details || {};
        base = {
          account_holder_name: d.account_holder_name || '',
          bank_account_number: d.bank_account_number || '',
          ifsc_code: d.ifsc_code || '',
          bank_name: d.bank_name || '',
          upi_id: d.upi_id || '',
        };
      } catch (_) {}
      let accounts = [];
      try {
        const h = await axios.get(`${API}/clients/${customer.id}/field-history`, { params: { field: 'bank' } });
        accounts = (h.data?.entries || [])
          .map((e) => ({ id: e.id, value: e.value || {}, is_active: e.is_active }))
          .filter((a) => a.value.bank_account_number || a.value.account_holder_name);
      } catch (_) {}
      if (cancelled) return;
      setSrBankAccounts(accounts);
      const active = accounts.find((a) => a.is_active) || accounts[0];
      if (active) {
        const v = active.value;
        setSrBankPickId(active.id);
        setSrBank({
          account_holder_name: v.account_holder_name || base.account_holder_name,
          bank_account_number: v.bank_account_number || base.bank_account_number,
          ifsc_code: v.ifsc_code || base.ifsc_code,
          bank_name: v.bank_name || base.bank_name,
          upi_id: base.upi_id,
        });
      } else {
        setSrBankPickId('');
        setSrBank(base);
      }
    })();
    return () => { cancelled = true; };
  }, [showSRCreateModal, customer?.chat_type, customer?.id]);


  // Point 50 — the keyboard must only open when the user explicitly taps
  // the message box, never automatically on opening a chat. If a search
  // box (or any other input) was focused on the previous screen, the
  // mobile keyboard can otherwise carry over into this page on mount.
  useEffect(() => {
    const active = document.activeElement;
    if (active && active !== document.body && typeof active.blur === 'function') {
      active.blur();
    }
  }, []);

  // Feature 17: Global paste listener — clipboard image opens MediaPicker preview.
  // Point 2 fix — works even when focus is INSIDE the message box. We no longer
  // early-return on input/textarea targets; instead we inspect the clipboard:
  // an image item is intercepted (preventDefault + preview), while plain text
  // is left untouched so normal text-paste keeps working exactly as before.
  useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file' && items[i].type.startsWith('image/')) {
          const blob = items[i].getAsFile();
          if (blob) {
            e.preventDefault();
            const file = new File([blob], `screenshot-${Date.now()}.png`, { type: blob.type || 'image/png' });
            setPastedFile(file);
            setShowMediaPicker(true);
          }
          break;
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);
  const [showSavedTextsPanel, setShowSavedTextsPanel] = useState(false);
  const [showSavedPhotosPanel, setShowSavedPhotosPanel] = useState(false);
  // Fix doc #6 — customer online/last-seen status (mirrored from HandlerChat).
  const [customerOnlineStatus, setCustomerOnlineStatus] = useState({ is_online: false, last_seen: null });
  // Offline-cache indicator — true when the browser reports a live network.
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerForm, setRegisterForm] = useState({ name: '', phone: '', password: 'Abcd1234' });
  const [registering, setRegistering] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editClientData, setEditClientData] = useState(null);
  const [hoveredMsg, setHoveredMsg] = useState(null);
  const [showForwardGroupModal, setShowForwardGroupModal] = useState(false);
  const [forwardGroupMsg, setForwardGroupMsg] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [pinnedMessages, setPinnedMessages] = useState([]); // point 43 — max 2
  const [pinDurationTarget, setPinDurationTarget] = useState(null);
  const [isChatMuted, setIsChatMuted] = useState(false); // point 52
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  
  // Reply & Forward features
  const [replyingTo, setReplyingTo] = useState(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [availableChats, setAvailableChats] = useState([]);
  const [messageToForward, setMessageToForward] = useState(null);
  
  // Image Preview State
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [allMediaUrls, setAllMediaUrls] = useState([]);
  const [currentMediaIdx, setCurrentMediaIdx] = useState(0);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const formRef = useRef(null);
  const menuRef = useRef(null);
  const longPressTimer = useRef(null);
  const [inputKey, setInputKey] = useState(0);
  const chatContainerRef = useRef(null);
  const containerRef = useRef(null);
  // Point-1 — measure the live chat-header height (profile row + actions strip)
  // so the message list / pinned banner offset by its ACTUAL height.
  const chatHeaderRef = useRef(null);
  const headerHeight = useElementHeight(chatHeaderRef, 96);
  // Pagination state — infinite scroll for messages (WhatsApp-style)
  const [olderLoading, setOlderLoading] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  // Session-32: replaces window.prompt() so mobile Chrome doesn't show the
  // "site.com says" URL header. Config: { open, title, helper, onConfirm }.
  const [reasonPrompt, setReasonPrompt] = useState(null);
  
  // Visual Viewport Keyboard Fix (Android Chrome + iOS Safari)
  // On mobile browsers, when the software keyboard opens, the *visual viewport*
  // shrinks and can scroll independently of the layout viewport. Without this
  // fix, the container stays anchored to layout top (0) while the visible
  // area is offset, so the input bar can drift up leaving a white gap above
  // the keyboard. We pin the container to the current visual viewport by
  // updating BOTH height and top on every resize+scroll event.
  useEffect(() => {
    const el = containerRef.current;
    const vv = window.visualViewport;
    if (!el || !vv) return;
    let lastHeight = vv.height;
    const apply = () => {
      el.style.height = `${vv.height}px`;
      el.style.top = `${vv.offsetTop}px`;
      // WhatsApp-style: keyboard just opened → scroll chat to the newest
      // message so it stays visible above the composer (session-32).
      if (vv.height < lastHeight - 100) {
        requestAnimationFrame(() => {
          try {
            const c = chatContainerRef.current;
            if (c) c.scrollTop = c.scrollHeight;
            if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ block: 'end' });
          } catch {}
        });
      }
      lastHeight = vv.height;
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      // Reset inline styles so a return to a non-chat screen restores defaults
      if (el) { el.style.height = ''; el.style.top = ''; }
    };
  }, []);
  
  // Track which messages have already played notification sound
  const notifiedMessageIds = useRef(new Set());
  
  // Ref to track previous messages count for notification logic
  const prevMessagesCountRef = useRef(0);

  // Reset notification tracking when switching between chats
  useEffect(() => {
    prevMessagesCountRef.current = 0;
    notifiedMessageIds.current.clear();
  }, [roomId]);

  // Listen for incoming calls
  useEffect(() => {
    const handleIncomingCall = (data) => {
      if (data.caller_id !== user?.id) {
        setIncomingCall({
          callId: data.call_id,
          callerId: data.caller_id,
          callerName: data.caller_name,
          callerProfilePic: data.caller_profile_pic,
          isConference: data.is_conference || false,
          participantNames: data.participant_names || []
        });
      }
    };
    socket.on('incoming_call', handleIncomingCall);
    return () => socket.off('incoming_call', handleIncomingCall);
  }, [user]);

  const EMOJIS = ['❤️', '👍', '😂', '😮', '😢', '🙏'];

  // Register customer from chat
  const handleRegisterCustomer = async () => {
    if (!registerForm.name || !registerForm.phone) {
      toast.error('Name and mobile number are required');
      return;
    }
    if (registerForm.phone.length !== 10) {
      toast.error('Mobile number must be 10 digits');
      return;
    }
    
    setRegistering(true);
    try {
      await axios.post(`${API}/inquiry/register-customer`, {
        guest_user_id: customer?.id || '',
        name: registerForm.name,
        phone: registerForm.phone,
        password: registerForm.password || 'Abcd1234',
        room_id: roomId,
        registered_by: user.id
      });
      
      toast.success('Customer registered successfully!');
      setShowRegisterModal(false);
      setRegisterForm({ name: '', phone: '', password: 'Abcd1234' });
      loadMessages();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to register customer';
      toast.error(msg);
    }
    setRegistering(false);
  };



  // Point 43 — fetch this room's pinned messages (up to 2)
  useEffect(() => {
    if (!roomId) return;
    let cancelled = false;
    (async () => {
      try {
        const roomRes = await axios.get(`${API}/chat/room/${roomId}`);
        const pins = roomRes.data?.pinned_messages || [];
        if (pins.length === 0) { if (!cancelled) setPinnedMessages([]); return; }
        const msgsRes = await axios.get(`${API}/chat/messages/${roomId}`);
        const pinIds = pins.map(p => p.message_id);
        const pinMsgs = pinIds.map(id => msgsRes.data.find(m => m.id === id)).filter(Boolean);
        if (!cancelled) setPinnedMessages(pinMsgs);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [roomId]);

  // Point 52 — mute-chat status for this room.
  useEffect(() => {
    if (!roomId || !user?.id) return;
    let cancelled = false;
    axios.get(`${API}/chat/room/${roomId}/mute-status`, { params: { user_id: user.id } })
      .then(r => { if (!cancelled) setIsChatMuted(!!r.data?.is_muted); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [roomId, user?.id]);

  // Memoized loadMessages function
  // Fix doc #6 — customer online status: initial fetch + live socket presence.
  useEffect(() => {
    if (!customer?.id) return;
    const fetchCustomerStatus = async () => {
      try {
        const res = await axios.get(`${API}/users/status/${customer.id}`);
        setCustomerOnlineStatus(prev => (prev.is_online === res.data.is_online && prev.last_seen === res.data.last_seen) ? prev : res.data);
      } catch (_) {}
    };
    fetchCustomerStatus();
    const interval = setInterval(fetchCustomerStatus, 20000);
    const handleOnline = (data) => { if (data?.user_id === customer.id) setCustomerOnlineStatus(prev => ({ ...prev, is_online: true })); };
    const handleOffline = (data) => { if (data?.user_id === customer.id) setCustomerOnlineStatus(prev => ({ ...prev, is_online: false, last_seen: new Date().toISOString() })); };
    onUserOnline(handleOnline);
    onUserOffline(handleOffline);
    return () => { clearInterval(interval); offUserOnline(handleOnline); offUserOffline(handleOffline); };
  }, [customer]);

  // Fix doc #1 — keep cached history fresh as messages change (incl. socket updates).
  useEffect(() => {
    if (roomId && Array.isArray(messages) && messages.length) {
      writeInstantCache('room_messages', roomId, messages.slice(-50));
    }
  }, [messages, roomId]);

  const loadMessages = useCallback(async () => {
    if (!roomId || !user?.id) return;
    
    try {
      // Fix doc #1 — instant paint from cached history on first open, then refresh below.
      const _cachedMsgs = readInstantCache('room_messages', roomId);
      if (_cachedMsgs && Array.isArray(_cachedMsgs)) {
        setMessages(prev => (prev && prev.length) ? prev : _cachedMsgs);
      }
      // IndexedDB durable cache — paint instantly even on the room's first open
      // of the session, after a full app close/reopen, or fully offline; the
      // network call below still runs afterward and overwrites with fresh data.
      idbGetMessages(roomId).then(idbMsgs => {
        if (idbMsgs && idbMsgs.length) {
          setMessages(prev => (prev && prev.length) ? prev : idbMsgs);
        }
      }).catch(() => {});
      // Pagination: load latest 50 messages initially (older loaded on scroll-up)
      const response = await axios.get(`${API}/chat/messages/${roomId}`, {
        params: { user_id: user.id, limit: 50 }
      });
      
      if (response.data && Array.isArray(response.data)) {
        console.log(`✅ Admin loaded ${response.data.length} messages for room ${roomId}`);
        // If fewer than page size returned, we know there are no more older
        setHasMoreOlder((response.data.length || 0) >= 50);
        
        // Check for new messages from customer and show notification
        const prevMessagesLength = prevMessagesCountRef.current;
        const newMessages = response.data;
        
        console.log(`📊 Admin notification check: prev=${prevMessagesLength}, new=${newMessages.length}`);
        console.log(`🔍 Will check for NEW messages: ${prevMessagesLength > 0 && newMessages.length > prevMessagesLength ? 'YES' : 'NO'}`);
        
        if (prevMessagesLength > 0 && newMessages.length > prevMessagesLength) {
          // Get only the NEW messages
          const newlyAddedMessages = newMessages.slice(prevMessagesLength);
          
          console.log(`🆕 Admin: Found ${newlyAddedMessages.length} NEW messages to check`);
          
          newlyAddedMessages.forEach((newMsg) => {
            console.log('📨 Admin checking message:', {
              id: newMsg.id,
              sender_id: newMsg.sender_id,
              sender_role: newMsg.sender_role,
              current_user_id: user.id,
              already_notified: notifiedMessageIds.current.has(newMsg.id)
            });
            
            // Only notify if message is from customer AND we haven't notified for this message yet
            if (newMsg.sender_id !== user.id && 
                newMsg.sender_role === 'customer' &&
                !notifiedMessageIds.current.has(newMsg.id)) {
              
              console.log('🔔🔔🔔 ADMIN NOTIFICATION TRIGGERED! Message:', newMsg.id);
              console.log('🎵 Calling showNotification with customer:', customer?.name);
              
              // Mark as notified
              notifiedMessageIds.current.add(newMsg.id);
              
              const messagePreview = newMsg.message_type === 'text' 
                ? newMsg.message_text 
                : newMsg.message_type === 'image' 
                ? '📷 Photo' 
                : newMsg.message_type === 'video'
                ? '🎥 Video'
                : newMsg.message_type === 'voice'
                ? '🎤 Voice message'
                : 'New message';
              
              console.log('📢 About to call showNotification...');
              showNotificationRef.current({
                title: `${customer?.name || 'Customer'} - DWS Vault Chat`,
                body: messagePreview,
                tag: `chat-${roomId}`,
                data: {
                  messageId: newMsg.id,
                  roomId: roomId,
                  senderId: newMsg.sender_id,
                  messageType: newMsg.message_type
                }
              });
              console.log('✅ showNotification called successfully');
            } else if (newMsg.sender_id === user.id || newMsg.sender_role !== 'customer') {
              console.log('ℹ️ Message from admin or self, skipping notification');
            } else if (notifiedMessageIds.current.has(newMsg.id)) {
              console.log('ℹ️ Already notified for this message, skipping');
            }
          });
        }
        
        // Update ref for next comparison
        prevMessagesCountRef.current = newMessages.length;
        // Auto-hide handoff_summary messages older than 24 hours
        const filtered = response.data.filter((m) => {
          if (m?.bot_type !== 'handoff_summary') return true;
          try {
            const age = (Date.now() - new Date(m.timestamp).getTime()) / 3600000;
            return age < 24;
          } catch { return true; }
        });
        // Merge (don't replace) by message id so a visibility-triggered
        // refetch can never resurrect a message the admin just deleted or
        // wipe the optimistic "deleted" state before the server catches up.
        setMessages(prev => {
          const prevById = new Map((prev || []).map(m => [m.id, m]));
          const merged = filtered.map(sm => {
            const pm = prevById.get(sm.id);
            if (pm && pm.deleted_for_everyone && !sm.deleted_for_everyone) {
              return { ...sm, deleted_for_everyone: true, is_deleted_view: true, deleted_by_meta: pm.deleted_by_meta };
            }
            return sm;
          });
          const serverIds = new Set(filtered.map(m => m.id));
          const pendingLocals = (prev || []).filter(m => m.pending && !serverIds.has(m.id));
          return pendingLocals.length ? [...merged, ...pendingLocals] : merged;
        });
        // Persist the fresh server response into the durable local cache.
        idbSaveMessages(roomId, response.data);
        
        // Mark all unread messages as read when chat is opened (global read)
        const unreadMessageIds = response.data
          .filter(msg => !msg.read && msg.sender_id !== user.id)
          .map(msg => msg.id);
        
        if (unreadMessageIds.length > 0) {
          try {
            // Socket.IO real-time read receipt
            markMessagesRead(roomId, unreadMessageIds, user.id);
            // HTTP API fallback
            await axios.post(`${API}/messages/mark-read`, {
              message_ids: unreadMessageIds
            }, {
              params: { user_id: user.id }
            });
            console.log(`✅ Marked ${unreadMessageIds.length} messages as read`);
          } catch (error) {
            console.error('Failed to mark messages as read:', error);
          }
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to load messages:', error);
      setLoading(false);
    }
  }, [roomId, user?.id]); // showNotification accessed via ref to prevent re-creation

  // Point 20 — on socket reconnect, silently refetch this chat's messages so a
  // message missed during the disconnect gap (or emitted to another pod) shows
  // without leave/reopen. Uses a ref to avoid stale closures.
  const loadMessagesRef = useRef(loadMessages);
  useEffect(() => { loadMessagesRef.current = loadMessages; }, [loadMessages]);
  const syncCtxRef = useRef({ roomId, userId: user?.id });
  useEffect(() => { syncCtxRef.current = { roomId, userId: user?.id }; });
  useEffect(() => {
    const onReconnect = async () => {
      try {
        const { roomId: rid, userId } = syncCtxRef.current || {};
        const did = await deltaSyncMessages({
          roomId: rid, userId,
          getMessages: () => messagesRef.current,
          setMessages,
        });
        if (!did && loadMessagesRef.current) loadMessagesRef.current();
      } catch (_) {
        try { loadMessagesRef.current && loadMessagesRef.current(); } catch (_) {}
      }
    };
    window.addEventListener('socket:reconnected', onReconnect);
    return () => window.removeEventListener('socket:reconnected', onReconnect);
  }, []);

  // Load OLDER messages when user scrolls to the top (WhatsApp-style pagination)
  const loadOlderMessages = useCallback(async () => {
    if (!roomId || !user?.id) return;
    if (olderLoading || !hasMoreOlder) return;
    if (!messages || messages.length === 0) return;
    const oldest = messages[0]?.timestamp;
    if (!oldest) return;

    setOlderLoading(true);
    const container = chatContainerRef.current;
    const prevHeight = container?.scrollHeight || 0;
    const prevTop = container?.scrollTop || 0;

    try {
      const resp = await axios.get(`${API}/chat/messages/${roomId}`, {
        params: { user_id: user.id, before: oldest, limit: 50 }
      });
      if (resp.data && Array.isArray(resp.data)) {
        const older = resp.data;
        if (older.length === 0) {
          setHasMoreOlder(false);
        } else {
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newOlder = older.filter(m => !existingIds.has(m.id));
            newOlder.forEach(m => notifiedMessageIds.current.add(m.id));
            return [...newOlder, ...prev];
          });
          setHasMoreOlder(older.length >= 50);
          requestAnimationFrame(() => {
            if (container) {
              const newHeight = container.scrollHeight;
              container.scrollTop = prevTop + (newHeight - prevHeight);
            }
          });
        }
      }
    } catch (e) {
      console.error('Failed to load older messages:', e);
    } finally {
      setOlderLoading(false);
    }
  }, [roomId, user?.id, olderLoading, hasMoreOlder, messages]);

  // Handle image preview
  const handleImageClick = (imageUrl) => {
    const mediaList = messages
      .filter(m => m.image_url)
      .map(m => {
        if (m.image_url.startsWith('http')) return m.image_url;
        if (m.image_url.startsWith('/api/')) return `${BACKEND_URL}${m.image_url}`;
        if (m.image_url.startsWith('/uploads/')) return `${BACKEND_URL}/api${m.image_url}`;
        return `${API}${m.image_url}`;
      });
    setAllMediaUrls(mediaList);
    const idx = mediaList.indexOf(imageUrl);
    setCurrentMediaIdx(idx >= 0 ? idx : 0);
    setPreviewImageUrl(imageUrl);
    setShowImagePreview(true);
  };

  const closeImagePreview = () => {
    setShowImagePreview(false);
    setPreviewImageUrl(null);
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  // Detect if user is scrolling up to read old messages + trigger load older near top
  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    setIsUserScrolling(distanceFromBottom > 100);
    
    // Near the top? Load older messages (WhatsApp-style infinite scroll)
    if (scrollTop < 120 && hasMoreOlder && !olderLoading) {
      loadOlderMessages();
    }
  };

  const prevMsgLenRef = useRef(0);
  // Reset scroll ref when switching chats
  useEffect(() => {
    prevMsgLenRef.current = 0;
  }, [roomId]);
  useEffect(() => {
    if (messages.length > 0) {
      if (prevMsgLenRef.current === 0) {
        scrollToBottom();
        requestAnimationFrame(scrollToBottom);
        setTimeout(scrollToBottom, 100);
        setTimeout(scrollToBottom, 500);
      } else if (messages.length > prevMsgLenRef.current && !isUserScrolling) {
        requestAnimationFrame(scrollToBottom);
      }
      prevMsgLenRef.current = messages.length;
    }
  }, [messages.length, roomId]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  // Load messages - initial load + visibility refresh (Socket.IO handles real-time)
  useEffect(() => {
    // Reset pagination state when switching rooms
    setHasMoreOlder(true);
    setOlderLoading(false);
    loadMessages();
    const cleanupVisibility = onVisibilityFetch(loadMessages);
    
    return () => {
      cleanupVisibility();
    };
  }, [loadMessages]);

  // Offline/cached indicator — track live network + run the 5-day IndexedDB
  // cleanup once when the chat mounts.
  useEffect(() => {
    idbCleanupOld();
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Socket.IO real-time messaging (alongside polling for zero-risk)
  useEffect(() => {
    if (!roomId || !user?.id) return;
    
    connectSocket(user.id);
    joinRoom(roomId, user.id);
    console.log('🔌 Socket.IO joined room:', roomId);
    
    const handleNewMessage = (data) => {
      if (data.room_id === roomId && data.message) {
        setMessages(prev => {
          // Dedup by message.id - polling may already have this message
          if (prev.some(m => m.id === data.message.id)) return prev;
          const cmid = data.message.client_message_id;
          if (cmid) {
            const idx = prev.findIndex(m => m.client_message_id === cmid && m.pending);
            if (idx !== -1) {
              const next = prev.slice();
              next[idx] = data.message;
              return next;
            }
          }
          return [...prev, data.message];
        });
        idbSaveMessages(roomId, [data.message]);
      }
    };

    const handleMessageUpdated = (data) => {
      if (data.room_id === roomId && data.message_id) {
        setMessages(prev => prev.map(m =>
          m.id === data.message_id
            ? { ...m,
                text: data.text ?? m.text,
                meta: { ...(m.meta || {}), ...(data.meta || {}) },
                edited_at: data.edited_at || m.edited_at,
                text_striked: data.text_striked !== undefined ? data.text_striked : m.text_striked,
                revert_reason: data.revert_reason !== undefined ? data.revert_reason : m.revert_reason,
                reverted_at: data.reverted_at || m.reverted_at }
            : m
        ));
      }
    };

    // Real-time delete sync — mark the message as deleted in place so admin
    // keeps the struck-through "deleted" context bubble (never a full remove,
    // never a content flash-back on the next refetch).
    const handleMessageDeleted = ({ message_id, delete_for_everyone }) => {
      if (!delete_for_everyone) {
        setMessages(prev => prev.filter(m => m.id !== message_id));
        idbDeleteMessage(message_id);
        return;
      }
      setMessages(prev => prev.map(m =>
        m.id === message_id
          ? { ...m, deleted_for_everyone: true, is_deleted_view: true }
          : m
      ));
      idbMarkDeleted(message_id);
    };

    // Real-time read receipts — customer/other staff read our messages → turn
    // ticks blue live (WhatsApp-style). Without this AdminChat only picked up
    // read-status on a full refetch (re-opening the chat), which is exactly the
    // "blue tick late / only after going back" symptom.
    const handleMessagesRead = (data) => {
      if (data.room_id !== roomId) return;
      if (Array.isArray(data.message_ids) && data.message_ids.length) {
        const ids = new Set(data.message_ids);
        setMessages(prev => prev.map(m =>
          ids.has(m.id) ? { ...m, delivered: true, read: true } : m
        ));
      } else if (data.auto || data.read_all) {
        // Some backend paths emit auto/read_all instead of an id list — mark all
        // of our own outgoing messages read.
        setMessages(prev => prev.map(m =>
          m.sender_id === user.id ? { ...m, delivered: true, read: true } : m
        ));
      }
    };

    const handleMessagesDelivered = (data) => {
      if (data.room_id !== roomId || !Array.isArray(data.message_ids)) return;
      const ids = new Set(data.message_ids);
      setMessages(prev => prev.map(m =>
        ids.has(m.id) ? { ...m, delivered: true } : m
      ));
    };

    onNewMessage(handleNewMessage);
    onMessageUpdated(handleMessageUpdated);
    onMessageDeleted(handleMessageDeleted);
    onMessagesRead(handleMessagesRead);
    onMessagesDelivered(handleMessagesDelivered);

    return () => {
      offNewMessage(handleNewMessage);
      offMessageUpdated(handleMessageUpdated);
      offMessageDeleted(handleMessageDeleted);
      offMessagesRead(handleMessagesRead);
      offMessagesDelivered(handleMessagesDelivered);
      leaveRoom(roomId, user.id);
    };
  }, [roomId, user?.id]);

  // Load customer info. Point 4 — this header data (name, phone, DP, MB
  // setup, chat-type label) is display-only, never a live financial value,
  // so it paints instantly from the last-known cache while the real
  // request runs silently behind it — fixes the "delay opening a chat"
  // symptom without ever showing stale money/status data (there isn't any
  // here to begin with).
  useEffect(() => {
    if (!roomId) return;

    const cached = readInstantCache('room_customer', roomId);
    if (cached) setCustomer(cached);
    // Durable IndexedDB fallback — paints header instantly even offline / after
    // a full app restart when the session cache above is empty.
    idbGetRoom(roomId).then(info => {
      if (info) setCustomer(prev => prev || info);
    }).catch(() => {});

    const loadCustomer = async () => {
      try {
        const roomResponse = await axios.get(`${API}/chat/room/${roomId}`);
        const room = roomResponse.data;

        if (room) {
          const fresh = {
            id: room.customer_id,
            name: room.customer_name,
            phone: room.customer_phone,
            profile_pic: room.customer_profile_pic,
            is_guest: room.customer_is_guest || false,
            role: room.customer_role || 'customer',
            mb_setup: room.mb_setup || null,
            chat_type: room.chat_type || null,
            flag: room.customer_flag || null,
            client_id: room.customer_client_id || null,
            client_address: room.customer_client_address || null
          };
          setCustomer(fresh);
          writeInstantCache('room_customer', roomId, fresh);
          idbSaveRoom(roomId, fresh);
        }
      } catch (error) {
        console.error('Failed to load customer:', error);
      }
    };

    loadCustomer();
  }, [roomId]);

  // Listen for real-time customer identity updates (name/phone/client_id/etc.).
  // Fires when admin edits a client via /clients/update/{id}; keeps every open
  // chat header in sync instantly — no more "old name comes back after 2-3 min".
  useEffect(() => {
    const onCustomerUpdated = (data) => {
      if (!data) return;
      const currentCustomerId = customer?.id;
      const sameRoom = data.room_id && data.room_id === roomId;
      const sameCust = data.customer_id && data.customer_id === currentCustomerId;
      if (!sameRoom && !sameCust) return;
      setCustomer((prev) => prev ? {
        ...prev,
        name: data.customer_name || prev.name,
        phone: data.customer_phone || prev.phone,
        client_id: data.customer_client_id !== undefined ? data.customer_client_id : prev.client_id,
        profile_pic: data.customer_profile_pic || prev.profile_pic,
        mb_setup: data.mb_setup !== undefined ? data.mb_setup : prev.mb_setup,
        flag: data.flag !== undefined ? data.flag : prev.flag,
      } : prev);
    };
    socket.on('customer_updated', onCustomerUpdated);
    return () => { socket.off('customer_updated', onCustomerUpdated); };
  }, [roomId, customer?.id]);

  const sendMessage = async (e, textFromInput = null) => {
    e?.preventDefault();
    
    // Use text from parameter (WhatsAppInput) or from inputRef (form submission)
    const messageText = textFromInput || inputRef.current?.value?.trim();
    
    if (!messageText || !roomId) {
      return;
    }

    // IMMEDIATE clear - WhatsAppInput handles its own clearing

    // Session-38 perf: optimistic send. Bubble appears instantly with a
    // grey "sending" tick and is swapped for the real doc once the server
    // acks. Matched via client_message_id so socket-echo dedups too.
    const clientMessageId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `cmid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const optimisticMsg = {
      id: `optimistic_${clientMessageId}`,
      client_message_id: clientMessageId,
      room_id: roomId,
      sender_id: user.id,
      sender_role: user.role,
      sender_name: user.username || 'You',
      text: messageText,
      image_url: null,
      voice_url: null,
      timestamp: new Date().toISOString(),
      read_status: 'sending',
      pending: true,
      reply_to: replyingTo ? {
        message_id: replyingTo.id,
        text: replyingTo.text || '(Media)',
        sender_name: replyingTo.sender_name,
      } : undefined,
    };
    setMessages(prev => [...prev, optimisticMsg]);
    setIsUserScrolling(false);
    const replyToSnapshot = replyingTo;
    setReplyingTo(null);

    try {
      // Prepare message data
      const messageData = {
        room_id: roomId,
        sender_id: user.id,
        text: messageText,
        image_url: null,
        voice_url: null,
        client_message_id: clientMessageId,
      };

      // Add reply reference if replying to a message
      if (replyToSnapshot) {
        messageData.reply_to = {
          message_id: replyToSnapshot.id,
          text: replyToSnapshot.text || '(Media)',
          sender_name: replyToSnapshot.sender_name
        };
      }

      const response = await axios.post(`${API}/chat/send-message`, messageData);

      setMessages(prev => {
        const withoutOptimistic = prev.filter(m => m.id !== optimisticMsg.id);
        if (withoutOptimistic.some(m => m.id === response.data.id)) return withoutOptimistic;
        return [...withoutOptimistic, response.data];
      });
      console.log('✅ Admin message sent:', messageText);
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => prev.map(m => (
        m.id === optimisticMsg.id ? { ...m, pending: false, send_failed: true, read_status: 'failed' } : m
      )));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  // Handler for WhatsAppInput send message
  const handleWhatsAppSendMessage = (text) => {
    sendMessage(null, text);
  };

  const handleProfileClick = () => {
    if (!customer) return;
    // #3 — desktop embedded chat opens the right-side Customer Details panel
    // instead of the full-screen profile. Mobile keeps the full profile.
    if (embeddedRoomId && shouldRenderDesktop()) {
      window.dispatchEvent(new CustomEvent('dws:open-customer-details', {
        detail: { customerId: customer.id, roomId: roomId, actorId: user.id },
      }));
      return;
    }
    navigate(`/handler-profile/${customer.id}?from_room=${roomId}`, { replace: true });
  };

  // Long press handlers
  const touchStartPos = useRef(null);
  
  const handleTouchStart = (messageId, e) => {
    // Don't trigger on child interactive elements
    if (e.target.tagName === 'IMG' || e.target.tagName === 'AUDIO' || e.target.closest('audio')) {
      return;
    }
    
    // Check if touches array exists
    if (!e.touches || e.touches.length === 0) {
      return;
    }
    // Skip long-press when the user is mid-pinch (2+ fingers) OR the page
    // is currently zoomed in — otherwise a normal pan gesture inside a
    // zoomed layout would falsely open the reaction/delete bar.
    if (e.touches.length > 1) return;
    try {
      const vv = window.visualViewport;
      if (vv && vv.scale > 1.05) return;
    } catch (_) {}
    
    // Store initial touch position
    const touch = e.touches[0];
    touchStartPos.current = {
      x: touch.clientX,
      y: touch.clientY
    };
    
    longPressTimer.current = setTimeout(() => {
      setLongPressMenu({
        messageId,
        x: touchStartPos.current.x,
        y: touchStartPos.current.y
      });
    }, 500);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    touchStartPos.current = null;
  };

  const handleTouchMove = (e) => {
    // Cancel long press only if finger moves more than 10px
    if (longPressTimer.current && touchStartPos.current) {
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = Math.abs(currentX - touchStartPos.current.x);
      const deltaY = Math.abs(currentY - touchStartPos.current.y);
      
      if (deltaX > 10 || deltaY > 10) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    }
  };

  // Double tap for emoji
  const lastTap = useRef(0);
  const handleDoubleTap = (messageId) => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      setShowEmojiPicker(messageId);
    }
    lastTap.current = now;
  };

  // Message actions
  const handleCopyMessage = (messageText) => {
    navigator.clipboard.writeText(messageText).then(() => {
      toast.success('Copied', { duration: 1500, style: { background: 'rgba(37,211,102,0.9)', color: '#fff', fontSize: '13px', padding: '6px 16px', minHeight: 'auto' } });
    }).catch(() => {});
    setLongPressMenu(null);
  };

  const handleDeleteClick = (messageId) => {
    setMessageToDelete(messageId);
    setLongPressMenu(null);
    setShowDeleteModal(true);
  };

  const handleDeleteMessage = async (deleteForEveryone) => {
    if (!messageToDelete) return;
    // Snapshot for undo / rollback
    const snapshot = messages.find(m => m.id === messageToDelete);
    const targetId = messageToDelete;
    setShowDeleteModal(false);
    setMessageToDelete(null);

    // Mark a message as the struck-through "deleted" audit bubble in place
    // (keeps the original content visible to admin as context — per spec).
    const markDeleted = (m) => ({
      ...m,
      deleted_for_everyone: true,
      is_deleted_view: true,
      deleted_by_meta: { by: user?.username || 'admin', role: user?.role || 'super_admin', at: new Date().toISOString() },
    });

    // Admin delete-for-everyone: 3-second undo window
    if (deleteForEveryone && user?.role === 'super_admin' && snapshot) {
      // Optimistic: transform bubble into "deleted" state instead of removing.
      setMessages(prev => prev.map(m => m.id === targetId ? markDeleted(m) : m));
      let undone = false;
      const timer = setTimeout(async () => {
        if (undone) return;
        try {
          await axios.delete(`${API}/chat/message/${targetId}`, {
            params: { user_id: user.id, delete_for_everyone: true }
          });
        } catch (e) {
          toast.error('Delete failed on server');
          setMessages(prev => prev.map(m => m.id === targetId ? snapshot : m));
        }
      }, 3000);
      toast('Message deleted', {
        duration: 3000,
        action: {
          label: 'UNDO',
          onClick: () => {
            undone = true;
            clearTimeout(timer);
            setMessages(prev => prev.map(m => m.id === targetId ? snapshot : m));
            toast.success('Delete undone');
          }
        }
      });
      return;
    }

    // Delete-for-self: remove from this admin's own view immediately.
    setMessages(prev => prev.filter(msg => msg.id !== targetId));
    try {
      await axios.delete(`${API}/chat/message/${targetId}`, {
        params: { user_id: user.id, delete_for_everyone: deleteForEveryone }
      });
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error(error?.response?.data?.detail || 'Failed to delete message');
      if (snapshot) setMessages(prev => prev.some(m => m.id === targetId) ? prev : [...prev, snapshot].sort((a,b) => new Date(a.timestamp) - new Date(b.timestamp)));
    }
  };

  const handleAddReaction = async (messageId, emoji) => {
    try {
      await axios.post(`${API}/chat/message/${messageId}/reaction`, null, {
        params: { user_id: user.id, emoji }
      });

      // Update local state
      setMessages(messages.map(msg => {
        if (msg.id === messageId) {
          const reactions = msg.reactions || {};
          if (reactions[emoji] && reactions[emoji].includes(user.id)) {
            reactions[emoji] = reactions[emoji].filter(id => id !== user.id);
            if (reactions[emoji].length === 0) delete reactions[emoji];
          } else {
            if (!reactions[emoji]) reactions[emoji] = [];
            reactions[emoji].push(user.id);
          }
          return { ...msg, reactions };
        }
        return msg;
      }));

      setShowEmojiPicker(null);
    } catch (error) {
      console.error('Failed to add reaction:', error);
    }
  };

  const handleReplyMessage = (msg) => {
    setReplyingTo(msg);
    setLongPressMenu(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const handleForward = async (messageId) => {
    try {
      const msg = messages.find(m => m.id === messageId);
      if (!msg) return;
      
      setMessageToForward(msg);
      
      // Point 7 — load BOTH chats and groups for the unified Forward modal.
      const [chatRes, groupRes] = await Promise.allSettled([
        axios.get(`${API}/chat/rooms`, { params: { user_id: user.id, role: user.role } }),
        axios.get(`${API}/groups/list`, { params: { user_id: user.id } }),
      ]);
      const otherChats = (chatRes.status === 'fulfilled' ? chatRes.value.data : []).filter(chat => chat.id !== roomId);
      setAvailableChats(otherChats);
      setAvailableGroups(groupRes.status === 'fulfilled' ? (groupRes.value.data || []) : []);
      setShowForwardModal(true);
      setLongPressMenu(null);
    } catch (error) {
      console.error('Failed to load forward targets:', error);
      toast.error('Failed to load chats');
    }
  };

  // Point 7 — unified forward to any mix of chats + groups.
  const forwardSelected = async ({ chatIds = [], groupIds = [] }) => {
    try {
      if (!messageToForward) return;
      const m = messageToForward;

      const chatTasks = chatIds.map((rid) =>
        axios.post(`${API}/chat/send-message`, {
          room_id: rid,
          sender_id: user.id,
          text: m.is_sticker ? '' : (m.text || '📷 Forwarded media'),
          image_url: m.image_url,
          voice_url: m.voice_url,
          is_sticker: !!m.is_sticker,
        })
      );
      const groupTasks = groupIds.map((gid) =>
        axios.post(`${API}/groups/${gid}/send-message`, {
          group_id: gid,
          sender_id: user.id,
          sender_name: user.username || user.name,
          sender_role: user.role,
          text: m.is_sticker ? '' : (m.text || ''),
          image_url: m.image_url || null,
          voice_url: m.voice_url || null,
          is_sticker: !!m.is_sticker,
          is_forwarded: true,
        })
      );

      const results = await Promise.allSettled([...chatTasks, ...groupTasks]);
      const okCount = results.filter((r) => r.status === 'fulfilled').length;
      const failCount = results.length - okCount;

      setShowForwardModal(false);
      setMessageToForward(null);

      if (okCount > 0 && failCount === 0) {
        toast.success(okCount === 1 ? 'Message forwarded!' : `Forwarded to ${okCount} chats/groups`);
      } else if (okCount > 0 && failCount > 0) {
        toast.warning(`Forwarded to ${okCount}, failed for ${failCount}`);
      } else {
        toast.error('Failed to forward');
      }
    } catch (error) {
      console.error('Forward failed:', error);
      toast.error('Failed to forward');
    }
  };



  const handleMediaSelected = async (media) => {
    try {
      // Determine text and URL based on media type
      let messageText = '';
      let imageUrl = null;
      let voiceUrl = null;
      let documentUrl = null;
      let documentName = null;
      let isSticker = false;
      
      if (media.type === 'image') {
        messageText = media.caption || '';
        imageUrl = media.url;
      } else if (media.type === 'video') {
        messageText = media.caption || '🎥 Video';
        imageUrl = media.url;
      } else if (media.type === 'voice') {
        messageText = '🎤 Voice';
        voiceUrl = media.url;
      } else if (media.type === 'sticker') {
        messageText = ''; // No text for stickers, just the image
        imageUrl = media.url;
        isSticker = true;
      } else if (media.type === 'document') {
        messageText = '📄 ' + (media.fileName || 'Document');
        documentUrl = media.url;
        documentName = media.fileName || 'Document';
      } else if (media.type === 'text') {
        messageText = media.text || '';
      }
      
      const response = await axios.post(`${API}/chat/send-message`, {
          room_id: roomId,
          sender_id: user.id,
          text: messageText,
          image_url: imageUrl,
          voice_url: voiceUrl,
          document_url: documentUrl,
          document_name: documentName,
          is_sticker: isSticker,
          client_message_id: media.client_message_id,
          view_once: media.view_once && (media.type === 'image' || media.type === 'video' || media.type === 'voice'),
        });

      setMessages(prev => {
        if (prev.some(m => m.id === response.data.id)) return prev;
        return [...prev, response.data];
      });
      setIsUserScrolling(false); // Scroll to bottom after sending media
    } catch (error) {
      console.error('Failed to send media:', error);
      toast.error('Failed to send media');
    }
  };

  // Loading state removed - UI renders instantly

  return (
    <div ref={containerRef} className={embeddedRoomId ? "relative flex-1 min-h-0 bg-gray-100 overflow-hidden" : "fixed inset-0 bg-gray-100 overflow-hidden"}>
      {/* Header - WhatsApp Style */}
      <div ref={chatHeaderRef} className="absolute top-0 left-0 right-0 bg-[#f0f2f5] border-b border-[#e9edef] z-30">
        <div className="flex items-center min-h-[56px] px-1">
          {/* Back Button */}
          <button
            onClick={() => {
              if (onBackToList) return onBackToList();
              // Point 10 — if we came from the full-screen N (New Members)
              // view, back must return there, NOT the dashboard.
              if (location.state?.fromNewMembers) return navigate('/new-members');
              // Explicit callers (e.g. Support Analytics, Service Timelines)
              // still win — Session-43 uses `replace: true` so that pressing
              // back again pops the caller ITSELF (settings/timelines), not
              // the chat we just left.
              if (location.state?.from) return navigate(location.state.from, { replace: true });
              // Session-35 (revised): Chat back = ALWAYS outer dashboard.
              // Dashboard state (activeTab/mbFilter) is restored from
              // sessionStorage — no flip. Earlier `navigate(-1)` broke when
              // user hopped chat → profile → chat and back landed on profile.
              navigate('/admin-dashboard', { state: { activeTab: location.state?.activeTab } });
            }}
            data-testid="admin-chat-back-button"
            className="p-2 hover:bg-gray-100 rounded-full text-gray-700"
          >
            <ArrowLeft size={24} />
          </button>

          {/* Profile Section */}
          <button
            onClick={handleProfileClick}
            className="flex items-center gap-3 flex-1 min-w-0 hover:bg-gray-50 py-1 px-2 rounded-lg transition-colors overflow-hidden"
          >
            {/* Profile Picture */}
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0" data-testid="chat-header-avatar">
              {customer?.profile_pic ? (
                <img 
                  src={`${API}${customer.profile_pic}`} 
                  alt={customer.name} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`w-full h-full flex items-center justify-center text-gray-600 font-semibold text-lg ${customer?.profile_pic ? 'hidden' : ''}`}>
                {customer?.name ? customer.name.charAt(0).toUpperCase() : 'C'}
              </div>
            </div>
            
            {/* Name and Status */}
            <div className="flex-1 text-left min-w-0">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2 min-w-0">
                <span className="truncate min-w-0" title={customer?.name || ''}>
                  {truncateName(customer?.name || 'Customer')}
                </span>
                {customer?.flag && (
                  <span className="flex-shrink-0"><FlagIcons flag={customer.flag} size={14} /></span>
                )}
                <span className="flex-shrink-0"><MBSetupBadge value={customer?.mb_setup} /></span>
                {customer?.client_id && (
                  <span
                    className="text-[9px] font-bold text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded-full uppercase tracking-wide max-w-[80px] truncate flex-shrink-0"
                    data-testid="chat-header-client-id"
                    title={`Client ID: ${customer.client_id}`}
                  >
                    {customer.client_id}
                  </span>
                )}
                {viewOnly && <Eye size={16} className="text-green-600 flex-shrink-0" />}
              </h2>
              {viewOnly && (
                <p className="text-xs text-gray-500 truncate">View Only (Inquiry)</p>
              )}
              {!viewOnly && (
                <OnlineStatus isOnline={customerOnlineStatus.is_online} lastSeen={customerOnlineStatus.last_seen} showText={true} textColor="text-gray-500" textSize="text-xs" />
              )}
              {(!isOnline) && (
                <span
                  className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold"
                  data-testid="offline-cache-indicator"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  Offline — showing saved chats
                </span>
              )}
              {customer?.client_address && (customer.client_address.city || customer.client_address.state) && (
                <p
                  className="text-[11px] text-blue-700 truncate leading-tight"
                  title={customer.client_address.formatted || ''}
                  data-testid="chat-header-client-address"
                >
                  📍 {[customer.client_address.city, customer.client_address.state].filter(Boolean).join(', ')}
                </p>
              )}
            </div>
          </button>

          {/* Assign-MB quick picker — rendered OUTSIDE the profile <button> so
              clicks don't get swallowed by the outer button (invalid nested
              buttons block the inner click in most browsers).
              Phase C: hidden entirely for "New User" (guest / no real account)
              chats — assign only appears once a real customer ID exists. */}
          {!viewOnly && !customer?.mb_setup && customer?.id && !customer?.is_guest && customer?.role === 'customer' && (
            <div className="mr-1 flex-shrink-0">
              <AssignMBQuickPicker
                customerId={customer.id}
                currentMB={customer?.mb_setup}
                actorId={user?.id}
                actorRole={user?.role}
                onAssigned={(mb) => setCustomer((prev) => (prev ? { ...prev, mb_setup: mb } : prev))}
              />
            </div>
          )}

          {/* Audio Call Button - Redirect to Customer's Phone Dialpad */}
          {!viewOnly && (
            <button
              onClick={() => {
                if (!customer?.phone) { toast.error('Customer phone not available'); return; }
                // Point 47 — log the tap as a staff-only chat entry (never
                // visible to the customer). Fire-and-forget so the dial pad
                // opens instantly; append it to this staff member's own
                // view optimistically since it isn't broadcast live.
                axios.post(`${API}/chat/room/${roomId}/call-log`, { user_id: user.id })
                  .then(res => setMessages(prev => (prev.some(m => m.id === res.data.id) ? prev : [...prev, res.data])))
                  .catch(() => {});
                window.location.href = `tel:${customer.phone}`;
              }}
              className="p-2 hover:bg-gray-100 rounded-full text-gray-700 flex-shrink-0"
              data-testid="audio-call-btn"
            >
              <Phone size={22} />
            </button>
          )}

          {/* Menu Button */}
          {!viewOnly && (
          <div ref={menuRef} className="relative flex-shrink-0">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 hover:bg-gray-100 rounded-full text-gray-700"
            >
              <DotsThreeVertical size={24} />
            </button>
            
            {showMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                {/* Session-48c: Create Request moved to top ChatHeaderActions
                    strip (with Coin Cut / Deposit). Cancel Request kept here
                    since it's a destructive action. */}
                {(customer?.chat_type === 'deposit' || customer?.chat_type === 'withdrawal' || customer?.chat_type === 'support') && (
                  <>
                    <button
                      onClick={async () => {
                        setShowMenu(false);
                        try {
                          const backendType = customer?.chat_type === 'withdrawal' ? 'withdraw' : customer?.chat_type;
                          const res = await axios.get(`${API}/service-requests/open/${customer?.id}`, { params: { type: backendType } });
                          const list = res.data?.items || [];
                          if (list.length === 0) { toast.error('No open requests to cancel'); return; }
                          setSrCancelList(list);
                          setShowSRCancelModal(true);
                        } catch (e) { toast.error('Failed to load'); }
                      }}
                      className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center gap-2"
                      data-testid="admin-sr-cancel-btn"
                    >
                      <span className="text-lg">❌</span> Cancel Request
                    </button>
                    <div className="border-t my-1" />
                  </>
                )}
                {/* Session-47: Theme toggle removed from customer 3-dot menu.
                    Users can change theme from Settings → Appearance instead. */}
                <button
                  onClick={() => {
                    handleProfileClick();
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <UserCircle size={20} />
                  View Profile
                </button>
                <button
                  onClick={async () => {
                    setShowMenu(false);
                    try {
                      const next = !isChatMuted;
                      await axios.post(`${API}/chat/room/${roomId}/mute`, { user_id: user.id, is_muted: next });
                      setIsChatMuted(next);
                      toast(next ? 'Chat muted' : 'Chat unmuted', { duration: 1500 });
                    } catch (e) { toast.error('Failed to update mute setting'); }
                  }}
                  className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  data-testid="mute-chat-btn"
                >
                  {isChatMuted ? <BellSlash size={20} /> : <Bell size={20} />}
                  {isChatMuted ? 'Unmute Chat' : 'Mute Chat'}
                </button>
                {customer?.is_guest && (
                  <button
                    onClick={() => {
                      setRegisterForm({ name: '', phone: '', password: 'Abcd1234' });
                      setShowRegisterModal(true);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-blue-600 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Plus size={20} />
                    Register Customer
                  </button>
                )}
                {!customer?.is_guest && customer?.role === 'customer' && (
                  <button
                    onClick={async () => {
                      setShowMenu(false);
                      try {
                        const res = await axios.get(`${API}/clients/registered`);
                        const fullClient = res.data.find(c => c.id === customer.id);
                        if (fullClient) {
                          setEditClientData(fullClient);
                          setShowEditModal(true);
                        } else {
                          toast.error('Client data not found');
                        }
                      } catch (e) {
                        toast.error('Failed to load client data');
                      }
                    }}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FileText size={20} />
                    Edit
                  </button>
                )}
                {!customer?.is_guest && customer?.role === 'customer' && (
                  <div className="border-t mt-1 pt-1">
                    <button
                      onClick={() => setShowMBMenu(!showMBMenu)}
                      className="w-full px-4 py-2 text-left text-teal-700 hover:bg-gray-100 flex items-center justify-between"
                      data-testid="assign-mb-menu-btn"
                    >
                      <span className="flex items-center gap-2">
                        <Star size={20} />
                        Assign to MB Setup
                      </span>
                      {customer?.mb_setup && <MBSetupBadge value={customer.mb_setup} />}
                    </button>
                    {showMBMenu && (
                      <div className="bg-gray-50 py-1">
                        {MB_SETUPS.map((mb) => (
                          <button
                            key={mb}
                            onClick={async () => {
                              try {
                                await axios.patch(`${API}/clients/${customer.id}/mb-setup`, {
                                  mb_setup: mb,
                                  actor_id: user.id
                                });
                                setCustomer((prev) => (prev ? { ...prev, mb_setup: mb } : prev));
                                toast.success(`Assigned to ${mb}`);
                                setShowMBMenu(false);
                                setShowMenu(false);
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Failed to assign MB setup');
                              }
                            }}
                            className={`w-full px-6 py-1.5 text-left text-sm hover:bg-teal-50 flex items-center justify-between ${customer?.mb_setup === mb ? 'font-semibold text-teal-700' : 'text-gray-700'}`}
                            data-testid={`assign-mb-${mb}`}
                          >
                            <span>{mb}</span>
                            {customer?.mb_setup === mb && <Check size={14} />}
                          </button>
                        ))}
                        {customer?.mb_setup && (
                          <button
                            onClick={async () => {
                              try {
                                await axios.patch(`${API}/clients/${customer.id}/mb-setup`, {
                                  mb_setup: null,
                                  actor_id: user.id
                                });
                                setCustomer((prev) => (prev ? { ...prev, mb_setup: null } : prev));
                                toast.success('MB tag removed');
                                setShowMBMenu(false);
                                setShowMenu(false);
                              } catch (e) {
                                toast.error('Failed to clear MB setup');
                              }
                            }}
                            className="w-full px-6 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
                            data-testid="assign-mb-clear"
                          >
                            Clear (remove tag)
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={() => {
                    toast('Block feature coming soon');
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-red-600 hover:bg-gray-100 flex items-center gap-2"
                >
                  <Prohibit size={20} />
                  Block Customer
                </button>
                {/* Session-48c: Mark-as-Solved moved into ChatHeaderActions
                    strip (support / inquiry chats only). */}
              </div>
            )}
          </div>
          )}
        </div>
        {customer?.id && (
          <div className="left-0 right-0">
            <ChatHeaderActions
              chatType={location.state?.chatTypeTab || customer.chat_type}
              actorId={user.id}
              actorRole={user.role}
              customerId={customer.id}
              roomId={roomId}
              customerFlag={customer?.flag || null}
              onFlagChanged={(next) => setCustomer((prev) => (prev ? { ...prev, flag: next } : prev))}
              onMessageSent={() => loadMessages && loadMessages()}
              onCreateRequest={() => { setSrAmount(''); setSrReference(''); setShowSRCreateModal(true); }}
              onMarkSolved={(reqId) => { setMarkSolvedReqId(reqId || null); setMarkSolvedOpen(true); }}
            />
          </div>
        )}
      </div>

      {false && pinnedMessages.length > 0 && (
        <div className="absolute left-0 right-0 bg-white border-b shadow-sm z-20" style={{ top: `${headerHeight}px` }} data-testid="pinned-message-banner">
          {pinnedMessages.map((pm) => (
            <div
              key={pm.id}
              className="px-4 py-2 flex items-center gap-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
              onClick={() => {
                const el = document.getElementById(`msg-${pm.id}`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
            >
              <PushPin size={18} className="text-blue-500 flex-shrink-0" weight="fill" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-blue-600 font-semibold">Pinned Message</p>
                <p className="text-sm text-gray-600 truncate">{pm.text || '📷 Media'}</p>
              </div>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await axios.delete(`${API}/chat/room/${roomId}/pin/${pm.id}`);
                    setPinnedMessages((prev) => prev.filter((m) => m.id !== pm.id));
                    toast('Unpinned', { duration: 1000 });
                  } catch (err) {}
                }}
                className="p-1 hover:bg-gray-200 rounded-full"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>
          ))}
        </div>
      )}
      {false && pinDurationTarget && (
        <PinDurationPicker
          onClose={() => setPinDurationTarget(null)}
          onPick={async (duration) => {
            try {
              await axios.post(`${API}/chat/room/${roomId}/pin`, { message_id: pinDurationTarget, user_id: user.id, duration });
              const msg = messages.find(m => m.id === pinDurationTarget);
              if (msg) setPinnedMessages(prev => [...prev, msg]);
              toast('Message pinned', { duration: 1500 });
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Failed to pin message');
            }
            setPinDurationTarget(null);
          }}
        />
      )}

      {/* Messages - Fixed position */}
      <div
        ref={chatContainerRef}
        onScroll={handleScroll}
        className="absolute left-0 right-0 overflow-y-auto p-3 bg-[#efeae2] overscroll-contain msg-noselect"
        style={{
          top: pinnedMessages.length > 0 ? `${headerHeight + pinnedMessages.length * 56}px` : `${headerHeight}px`,
          bottom: '56px',
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'412\' height=\'412\' viewBox=\'0 0 412 412\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23d6d0c5\' fill-opacity=\'0.4\'%3E%3Ccircle cx=\'20\' cy=\'20\' r=\'2\'/%3E%3Ccircle cx=\'100\' cy=\'50\' r=\'1.5\'/%3E%3Ccircle cx=\'200\' cy=\'30\' r=\'2\'/%3E%3Ccircle cx=\'300\' cy=\'60\' r=\'1.5\'/%3E%3Ccircle cx=\'60\' cy=\'120\' r=\'2\'/%3E%3Ccircle cx=\'160\' cy=\'100\' r=\'1.5\'/%3E%3Ccircle cx=\'260\' cy=\'130\' r=\'2\'/%3E%3Ccircle cx=\'360\' cy=\'110\' r=\'1.5\'/%3E%3Ccircle cx=\'40\' cy=\'200\' r=\'2\'/%3E%3Ccircle cx=\'140\' cy=\'220\' r=\'1.5\'/%3E%3Ccircle cx=\'240\' cy=\'190\' r=\'2\'/%3E%3Ccircle cx=\'340\' cy=\'210\' r=\'1.5\'/%3E%3Ccircle cx=\'80\' cy=\'300\' r=\'2\'/%3E%3Ccircle cx=\'180\' cy=\'280\' r=\'1.5\'/%3E%3Ccircle cx=\'280\' cy=\'310\' r=\'2\'/%3E%3Ccircle cx=\'380\' cy=\'290\' r=\'1.5\'/%3E%3Ccircle cx=\'50\' cy=\'380\' r=\'2\'/%3E%3Ccircle cx=\'150\' cy=\'360\' r=\'1.5\'/%3E%3Ccircle cx=\'250\' cy=\'390\' r=\'2\'/%3E%3Ccircle cx=\'350\' cy=\'370\' r=\'1.5\'/%3E%3C/g%3E%3C/svg%3E")'
        }}
      >
        <div className="space-y-[3px] pb-4">
          {/* Older messages loader (top of chat) */}
          {olderLoading && (
            <div className="flex items-center justify-center py-2" data-testid="older-loading">
              <div className="bg-white/90 text-[#54656f] text-[11px] font-medium px-3 py-1 rounded-full shadow-sm inline-flex items-center gap-2">
                <span className="w-3 h-3 border-2 border-[#54656f] border-t-transparent rounded-full animate-spin"></span>
                Loading older messages…
              </div>
            </div>
          )}
          {!hasMoreOlder && messages.length > 0 && (
            <div className="flex items-center justify-center py-2" data-testid="older-end">
              <div className="bg-white/70 text-[#54656f] text-[10px] font-medium px-3 py-1 rounded-full">
                Start of conversation
              </div>
            </div>
          )}
          {/* Session-38 perf: skeleton bubbles while first fetch is in flight
              so the chat doesn't flash blank. */}
          {messages.length === 0 && !loading && (
            <div className="space-y-2 py-4" data-testid="chat-skeleton">
              {[
                { align: 'start', w: 'w-40' },
                { align: 'end',   w: 'w-56' },
                { align: 'start', w: 'w-52' },
                { align: 'end',   w: 'w-36' },
                { align: 'start', w: 'w-44' },
              ].map((s, i) => (
                <div key={i} className={`flex ${s.align === 'end' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`${s.w} h-8 rounded-lg animate-pulse ${
                    s.align === 'end' ? 'bg-[#d9fdd3]/60' : 'bg-white/70'
                  }`} />
                </div>
              ))}
            </div>
          )}
          {messages.map((msg, idx) => {
            const isOwn = msg.sender_id === user.id || (msg.sender_role && !['customer', 'inquiry_guest', 'support_guest'].includes(msg.sender_role));
            const reactions = msg.reactions || {};
            
            // Skip if part of image group but not first
            if (isPartOfImageGroup(messages, idx)) return null;
            let imageGroup = getImageGroup(messages, idx);
            // Session-53 defence-in-depth — render-time filter (same as HandlerChat).
            if (imageGroup) {
              imageGroup = imageGroup.filter(g => !isStickerLike(g));
              if (imageGroup.length < 2) imageGroup = null;
            }
            
            // Check if this is the first unread INCOMING message (not own message)
            const isIncoming = !isOwn;
            const isPreviousIncoming = idx > 0 ? (() => { const prev = messages[idx - 1]; return prev?.sender_role ? ['customer', 'inquiry_guest', 'support_guest'].includes(prev.sender_role) : prev?.sender_id !== user.id; })() : false;
            const isFirstUnread = isIncoming && !msg.read && (idx === 0 || messages[idx - 1]?.read !== false || !isPreviousIncoming);
            const unreadCount = messages.filter(m => {
              const mIsOwn = m.sender_id === user.id || (m.sender_role && !['customer', 'inquiry_guest', 'support_guest'].includes(m.sender_role));
              return !mIsOwn && !m.read;
            }).length;
            
            return (
              <React.Fragment key={idx}>
                {/* Date Separator - WhatsApp Style */}
                {shouldShowDateSeparator(messages, idx) && (
                  <div className="flex items-center justify-center my-3">
                    <div className="bg-white/90 text-[#54656f] text-[11px] font-medium px-3 py-1 rounded-lg shadow-sm" data-testid="date-separator">
                      {getDateLabel(msg.timestamp)}
                    </div>
                  </div>
                )}

                {/* Unread Messages Divider - Only for incoming messages */}
                {isFirstUnread && unreadCount > 0 && (
                  <div className="flex items-center justify-center my-4">
                    <div className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
                      {unreadCount} Unread Message{unreadCount > 1 ? 's' : ''}
                    </div>
                  </div>
                )}
                
              <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                <div 
                  className="relative max-w-[85%] group"
                  onMouseEnter={() => setHoveredMsg(msg.id)}
                  onMouseLeave={() => setHoveredMsg(null)}
                >
                  {/* Dropdown arrow */}
                  {hoveredMsg === msg.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setLongPressMenu({
                          messageId: msg.id,
                          x: e.clientX,
                          y: e.clientY
                        });
                      }}
                      className="absolute top-1 right-1 z-10 w-5 h-5 flex items-center justify-center rounded-full bg-black/10 hover:bg-black/20 transition-opacity"
                    >
                      <CaretDown size={12} className="text-gray-600" weight="bold" />
                    </button>
                  )}
                  <div 
                    className={`relative ${msg.is_sticker
                      ? 'bg-transparent shadow-none p-0'
                      : `px-2 pt-1.5 pb-[2px] rounded-lg shadow-sm overflow-hidden ${isOwn ? 'bg-[#d9fdd3]' : 'bg-white'}`
                    }`}
                    style={{
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                      WebkitTouchCallout: 'none',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                    onContextMenu={(e) => { e.preventDefault(); handleTouchStart(msg.id, e); }}
                    onTouchStart={(e) => handleTouchStart(msg.id, e)}
                    onTouchEnd={handleTouchEnd}
                    onTouchMove={handleTouchMove}
                    onMouseDown={(e) => handleTouchStart(msg.id, { target: e.target, touches: [{ clientX: e.clientX, clientY: e.clientY }] })}
                    onMouseUp={handleTouchEnd}
                    onMouseLeave={handleTouchEnd}
                    onClick={() => handleDoubleTap(msg.id)}
                  >
                    {/* Reply Reference - WhatsApp Style */}
                    {msg.reply_to && (
                      <div className={`mb-1 pl-2 py-0.5 border-l-2 rounded text-xs ${
                        isOwn ? 'border-green-700 bg-green-100/30' : 'border-blue-600 bg-blue-100/30'
                      }`}>
                        <div className="font-semibold text-[10px] opacity-70">
                          {msg.reply_to.sender_name}
                        </div>
                        <div className="truncate text-[11px] opacity-60">
                          {msg.reply_to.text || '📷 Media'}
                        </div>
                      </div>
                    )}
                    
                    {/* Deleted banner for MEDIA / non-text messages (image, video,
                        voice, document without caption) — text messages already
                        render their own badge inside the text block below. */}
                    {msg.deleted_for_everyone && !(msg.text && !msg.call_info) && (
                      <div className="mb-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold uppercase tracking-wide" data-testid={`deleted-badge-${msg.id}`}>
                        ✕ Deleted by {msg.deleted_by_meta?.by || 'user'}
                        {msg.deleted_by_meta?.at && <span className="opacity-80"> · {new Date(msg.deleted_by_meta.at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
                      </div>
                    )}
                    
                    {/* Image */}
                    {/* Image Group Grid */}
                    {imageGroup && imageGroup.length > 1 && (
                      <div className={`grid grid-cols-2 gap-0.5 rounded-lg overflow-hidden mb-1.5 w-[320px] max-w-full ${msg.deleted_for_everyone ? 'opacity-50 grayscale ring-2 ring-red-500' : ''}`}>
                        {imageGroup.slice(0, 4).map((gMsg, gIdx) => {
                          const gUrl = gMsg.image_url.startsWith('http') ? gMsg.image_url 
                            : gMsg.image_url.startsWith('/api/') ? `${BACKEND_URL}${gMsg.image_url}` 
                            : `${BACKEND_URL}/api${gMsg.image_url}`;
                          return (
                            <div key={gIdx} className="relative cursor-pointer" onClick={() => handleImageClick(gUrl)}>
                              <img src={gUrl} alt="" className="w-full h-[120px] object-cover" />
                              {gIdx === 3 && imageGroup.length > 4 && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                  <span className="text-white text-2xl font-bold">+{imageGroup.length - 4}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Group caption below grid */}
                    {imageGroup && imageGroup.length > 1 && (() => {
                      const lastMsg = imageGroup[imageGroup.length - 1];
                      const captionText = lastMsg.caption || (lastMsg.text && lastMsg.text.trim() && lastMsg.text !== '🖼️ Photo' && lastMsg.text !== '🎥 Video' ? lastMsg.text : '');
                      return captionText ? <p className="text-sm mb-1">{captionText}</p> : null;
                    })()}

                    {/* Single Image or Video — WhatsApp style: flush edges, overlay timestamp when no caption */}
                    {/* Point 63 — view-once placeholder */}
                    {msg.view_once_state && !imageGroup && msg.sender_id !== user.id && msg.view_once_media_type !== 'voice' && (
                      <ViewOnceBubble msg={msg} userId={user.id} openUrl={`${API}/chat/message/${msg.id}/view-once/open`} />
                    )}
                    {msg.image_url && !imageGroup && (() => {
                      const hasCaption = !!(msg.caption || (msg.text && msg.text.trim() && msg.text !== '🖼️ Photo' && msg.text !== '🎥 Video'));
                      const timeStr = new Date((/[Zz]|[+-]\d\d:?\d\d$/.test(msg.timestamp) ? msg.timestamp : (msg.timestamp||'')+'Z')).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
                      // Stickers render as a compact sticker cell — no bubble
                      // background, ~140px, transparent, sits like an emoji.
                      if (msg.is_sticker) {
                        return (
                          <img
                            src={buildMediaUrl(msg.image_url)}
                            alt="Sticker"
                            className={`w-[88px] max-w-[88px] h-auto object-contain block bg-transparent ${msg.deleted_for_everyone ? 'opacity-50 grayscale ring-2 ring-red-500 rounded' : ''}`}
                            data-testid="sticker-message"
                          />
                        );
                      }
                      return (
                        <div className={`relative -mx-2 -mt-1.5 ${hasCaption ? 'mb-1' : 'mb-0'} rounded-t-lg overflow-hidden ${msg.deleted_for_everyone ? 'opacity-50 grayscale ring-2 ring-red-500' : ''}`}>
                          {/* Point 40 — no `w-full`/`object-cover` here: a
                              single photo/video renders at its own natural
                              aspect ratio (capped by max-w/max-h), matching
                              WhatsApp, instead of being stretched to the
                              bubble's full width and cropped to fit. */}
                          {isVideoUrl(msg.image_url) ? (
                            <video
                              src={buildMediaUrl(msg.image_url)}
                              controls
                              className="max-w-[280px] max-h-[320px] block"
                              preload="metadata"
                              data-testid="video-message"
                            />
                          ) : (
                            <img
                              src={buildMediaUrl(msg.image_url)}
                              alt="Shared"
                              className="w-[180px] h-[180px] object-cover block cursor-pointer hover:opacity-95 transition-opacity"
                              onClick={() => handleImageClick(buildMediaUrl(msg.image_url))}
                              data-testid="photo-message"
                            />
                          )}
                          {/* WhatsApp-style timestamp overlay (only when no caption) */}
                          {!hasCaption && !isVideoUrl(msg.image_url) && (
                            <div className="absolute bottom-1 right-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/45 backdrop-blur-[2px]">
                              <span className="text-[10.5px] text-white leading-none">{timeStr}</span>
                              {isOwn && (() => {
                                if (msg.send_failed) return <WarningCircle size={14} weight="bold" className="text-red-400" />;
                                if (msg.pending) return <Clock size={12} weight="regular" className="text-white/80" />;
                                const delivered = msg.delivered !== undefined ? msg.delivered : true;
                                const read = msg.read !== undefined ? msg.read : false;
                                if (read) return <Checks size={13} weight="bold" className="text-[#8ee8ff]" />;
                                if (delivered) return <Checks size={13} weight="bold" className="text-white/80" />;
                                return <Check size={13} weight="bold" className="text-white/80" />;
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    
                    {/* Point 63 — view-once voice placeholder */}
                    {msg.view_once_state && msg.sender_id !== user.id && msg.view_once_media_type === 'voice' && (
                      <ViewOnceBubble msg={msg} userId={user.id} openUrl={`${API}/chat/message/${msg.id}/view-once/open`} />
                    )}

                    {/* Voice Message */}
                    {msg.voice_url && (
                      <div className={msg.deleted_for_everyone ? 'opacity-50 ring-2 ring-red-500 rounded-lg' : ''}>
                      <VoiceMessage
                        audioUrl={(() => {
                          if (msg.voice_url.startsWith('http')) {
                            return msg.voice_url;
                          } else if (msg.voice_url.startsWith('/api/')) {
                            return `${BACKEND_URL}${msg.voice_url}`;
                          } else if (msg.voice_url.startsWith('/uploads/')) {
                            return `${BACKEND_URL}/api${msg.voice_url}`;
                          } else {
                            return `${API}${msg.voice_url}`;
                          }
                        })()}
                        isSender={isOwn}
                      />
                      </div>
                    )}
                    
                    {/* Document Display - Clickable */}
                    {msg.document_url && (
                      <a
                        href={(() => {
                          if (msg.document_url.startsWith('http')) {
                            return msg.document_url;
                          } else if (msg.document_url.startsWith('/api/')) {
                            return `${BACKEND_URL}${msg.document_url}`;
                          } else if (msg.document_url.startsWith('/uploads/')) {
                            return `${BACKEND_URL}/api${msg.document_url}`;
                          } else {
                            return `${API}${msg.document_url}`;
                          }
                        })()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-2 p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors ${msg.deleted_for_everyone ? 'opacity-50 ring-2 ring-red-500' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileText size={24} className="text-white" weight="fill" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {msg.document_name || 'Document'}
                          </p>
                          <p className="text-xs text-gray-500">Tap to open</p>
                        </div>
                      </a>
                    )}
                    
                    {/* Call Info Bubble - WhatsApp Style */}
                    {msg.call_info ? (
                      <div className="flex items-center gap-2 py-1">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${msg.call_info.status === 'completed' ? 'bg-green-100' : 'bg-red-100'}`}>
                          <Phone size={16} weight="fill" className={`${msg.call_info.status === 'completed' ? 'text-green-600' : 'text-red-500'} ${msg.call_info.caller_id === user?.id ? 'rotate-[135deg]' : ''}`} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">Voice call</p>
                          <p className={`text-xs ${msg.call_info.status === 'completed' ? 'text-gray-500' : 'text-red-500'}`}>
                            {msg.call_info.status === 'completed' ? (msg.call_info.duration >= 60 ? `${Math.floor(msg.call_info.duration/60)} min` : `${msg.call_info.duration} secs`) : 'No answer'}
                          </p>
                        </div>
                      </div>
                    ) : null}
                    
                    {/* Action Sticker Bubble (Withdraw Done / Deposit Done / Coin Cut) */}
                    {msg.action_sticker && (
                      <div className="py-1"><ActionStickerBubble sticker={msg.action_sticker} /></div>
                    )}
                    
                    {/* Text + WhatsApp-style inline time */}
                    {msg.text && !msg.call_info ? (
                      <div
                        className={`leading-[19px] ${msg.deleted_for_everyone ? 'border-l-4 border-red-500 pl-2 pr-1 py-1 bg-red-50/60 rounded' : ''} ${msg.text_striked ? 'bg-red-50 border-l-4 border-red-500 pl-2 pr-1 py-1 rounded' : ''}`}
                        data-testid={msg.deleted_for_everyone ? `deleted-msg-${msg.id}` : (msg.text_striked ? `reverted-msg-${msg.id}` : undefined)}
                      >
                        {msg.text_striked && (
                          <div className="mb-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold uppercase tracking-wide">
                            ❌ Reverted
                          </div>
                        )}
                        {msg.deleted_for_everyone && (
                          <div className="mb-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold uppercase tracking-wide" data-testid={`deleted-badge-${msg.id}`}>
                            ✕ Deleted by {msg.deleted_by_meta?.by || 'user'}
                            {msg.deleted_by_meta?.at && <span className="opacity-80"> · {new Date(msg.deleted_by_meta.at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
                          </div>
                        )}
                        {msg.meta?.bot_options?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-1.5 mb-1" data-testid={`bot-options-${msg.id}`}>
                            {msg.meta.bot_options.map((op, oi) => (
                              op.link ? (
                                <a key={oi} href={op.link} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 text-[11px] font-semibold hover:bg-emerald-100"
                                  data-testid={`bot-option-${msg.id}-${oi}`}>
                                  {op.label || `Option ${oi + 1}`}
                                </a>
                              ) : (
                                <span key={oi}
                                  className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-100 border border-slate-300 text-slate-700 text-[11px] font-semibold"
                                  title={op.reply || ''}
                                  data-testid={`bot-option-${msg.id}-${oi}`}>
                                  {op.label || `Option ${oi + 1}`}
                                </span>
                              )
                            ))}
                          </div>
                        )}
                        <span className={`text-[14.5px] break-words whitespace-pre-line ${msg.deleted_for_everyone ? 'line-through text-red-800/80' : 'text-[#111b21]'} ${msg.text_striked ? 'line-through text-red-800/80' : ''}`}>
                          {msg.text.split(/(https?:\/\/[^\s]+)/g).map((part, index) => {
                            if (part.match(/^https?:\/\//)) {
                              return (
                                <a
                                  key={index}
                                  href={part}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#027eb5] underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {part}
                                </a>
                              );
                            }
                            return part;
                          })}
                        </span>
                        {/* Session-43: Edit / Cancel-deposit / Cancelled-tag
                            controls on deposit_done / coin_cut / withdraw_done
                            notices REMOVED per user request. These messages
                            are now immutable — cancel flow lives in the
                            pending-requests chooser above. */}
                        {null}
                        <span className="float-right ml-[8px] mt-[3px] flex items-center gap-[3px] leading-none">
                          {msg.starred_by?.includes(user.id) && <Star size={11} weight="fill" className="text-yellow-500" />}
                          {msg.edited && <span className="text-[10.5px] text-[#667781] italic">edited</span>}
                          <span className="text-[11px] text-[#667781]">{new Date((/[Zz]|[+-]\d\d:?\d\d$/.test(msg.timestamp) ? msg.timestamp : (msg.timestamp||'')+'Z')).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}</span>
                          {isOwn && (() => {
                            if (msg.send_failed) return <WarningCircle size={16} weight="bold" className="text-red-500" />;
                            if (msg.pending) return <Clock size={14} weight="regular" className="text-[#667781]" />;
                            const delivered = msg.delivered !== undefined ? msg.delivered : true;
                            const read = msg.read !== undefined ? msg.read : false;
                            if (read) return <Checks size={16} weight="bold" className="text-[#53bdeb]" />;
                            if (delivered) return <Checks size={16} weight="bold" className="text-[#667781]" />;
                            return <Check size={16} weight="bold" className="text-[#667781]" />;
                          })()}
                        </span>
                      </div>
                    ) : (
                      <div className="flex justify-end items-center gap-[3px] mt-[2px] mb-[1px]">
                        {msg.starred_by?.includes(user.id) && <Star size={11} weight="fill" className="text-yellow-500" />}
                        {msg.edited && <span className="text-[10.5px] text-[#667781] italic">edited</span>}
                        <span className="text-[11px] text-[#667781] leading-none">{new Date((/[Zz]|[+-]\d\d:?\d\d$/.test(msg.timestamp) ? msg.timestamp : (msg.timestamp||'')+'Z')).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}</span>
                        {isOwn && (() => {
                          const delivered = msg.delivered !== undefined ? msg.delivered : true;
                          const read = msg.read !== undefined ? msg.read : false;
                          if (read) return <Checks size={16} weight="bold" className="text-[#53bdeb]" />;
                          if (delivered) return <Checks size={16} weight="bold" className="text-[#667781]" />;
                          return <Check size={16} weight="bold" className="text-[#667781]" />;
                        })()}
                      </div>
                    )}
                  </div>
                  
                  {/* Reactions */}
                  {Object.keys(reactions).length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {Object.entries(reactions).map(([emoji, userIds]) => (
                        <div 
                          key={emoji}
                          className="bg-white rounded-full px-2 py-1 text-xs shadow-sm border flex items-center gap-1"
                        >
                          <span>{emoji}</span>
                          <span className="text-gray-600">{userIds.length}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Emoji Picker */}
                  {showEmojiPicker === msg.id && (
                    <div className="absolute bottom-full mb-2 bg-white rounded-lg shadow-lg p-2 flex gap-2 z-50">
                      {EMOJIS.map(emoji => (
                        <button
                          key={emoji}
                          onClick={() => handleAddReaction(msg.id, emoji)}
                          className="text-2xl hover:scale-125 transition-transform"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              </React.Fragment>
            );
          })}
          <div ref={messagesEndRef} style={{ overflowAnchor: 'auto', height: '1px' }} />
        </div>
      </div>

      {/* Scroll to Bottom Arrow - WhatsApp Style */}
      {isUserScrolling && (
        <button
          onClick={() => { setIsUserScrolling(false); scrollToBottom(); }}
          className={`${embeddedRoomId ? 'absolute' : 'fixed'} bottom-24 right-4 z-20 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border border-gray-200`}
          data-testid="scroll-to-bottom-btn"
        >
          <CaretDown size={20} className="text-[#54656f]" weight="bold" />
        </button>
      )}

      {/* Input - WhatsApp Style */}
      <div className="absolute bottom-0 left-0 right-0 z-30 pb-[env(safe-area-inset-bottom)]">
        {/* View Only Banner for Inquiry Chats - Green theme */}
        {viewOnly ? (
          <div className="bg-white border-t p-3">
            <div className="bg-green-50 border border-green-200 text-green-700 py-3 px-4 flex items-center justify-center gap-2 rounded-lg shadow-sm">
              <Eye size={20} weight="bold" />
              <span className="font-medium text-sm">View Only Mode - You are viewing this inquiry conversation</span>
            </div>
          </div>
        ) : (
          <WhatsAppInput
            onSendMessage={handleWhatsAppSendMessage}
            onSendMedia={handleMediaSelected}
            onOpenMediaPicker={() => setShowMediaPicker(true)}
            placeholder="Type a message..."
            disabled={false}
            replyingTo={replyingTo}
            onCancelReply={cancelReply}
            userId={user?.id}
            userRole="admin"
          />
        )}
      </div>

      {/* Long Press Menu — WhatsApp-style compact icon bar */}
      {longPressMenu && (() => {
        const msg = messages.find(m => m.id === longPressMenu.messageId);
        if (!msg) return null;
        const isStarred = msg?.starred_by?.includes(user.id);
        const isOwn = msg.sender_id === user.id;
        const barTop = Math.max(60, longPressMenu.y - 70);
        const barLeft = Math.max(12, Math.min(longPressMenu.x - 140, window.innerWidth - 292));
        return (
          <>
            <div className="fixed inset-0 z-40" onClick={() => { setLongPressMenu(null); setContextMoreOpen(false); }} />
            {/* Horizontal icon bar */}
            <div
              className="fixed bg-white rounded-full shadow-2xl border border-gray-200 z-50 flex items-center px-1.5 py-1"
              style={{ top: barTop, left: barLeft }}
              data-testid="long-press-icon-bar"
            >
              <button
                onClick={() => handleReplyMessage(msg)}
                className="p-2.5 hover:bg-gray-100 rounded-full text-gray-700"
                title="Reply" data-testid="lp-reply"
              >
                <ArrowBendUpLeft size={20} weight="bold" />
              </button>
              <button
                onClick={() => handleForward(longPressMenu.messageId)}
                className="p-2.5 hover:bg-gray-100 rounded-full text-gray-700"
                title="Forward" data-testid="lp-forward"
              >
                <ShareFat size={20} weight="bold" />
              </button>
              <button
                onClick={() => { handleCopyMessage(msg.text); }}
                className="p-2.5 hover:bg-gray-100 rounded-full text-gray-700"
                title="Copy" data-testid="lp-copy"
              >
                <Copy size={20} weight="bold" />
              </button>
              <button
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/chat/message/${longPressMenu.messageId}/star?user_id=${user.id}`);
                    setMessages(prev => prev.map(m => {
                      if (m.id === longPressMenu.messageId) {
                        const starred = m.starred_by || [];
                        return res.data.starred
                          ? { ...m, starred_by: [...starred, user.id] }
                          : { ...m, starred_by: starred.filter(id => id !== user.id) };
                      }
                      return m;
                    }));
                    toast(res.data.starred ? 'Message starred' : 'Star removed', { duration: 1500 });
                  } catch (err) { toast.error('Failed to star message'); }
                  setLongPressMenu(null);
                }}
                className="p-2.5 hover:bg-gray-100 rounded-full text-gray-700"
                title="Star" data-testid="lp-star"
              >
                <Star size={20} weight={isStarred ? 'fill' : 'bold'} className={isStarred ? 'text-yellow-500' : ''} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setContextMoreOpen(v => !v); }}
                className="p-2.5 hover:bg-gray-100 rounded-full text-gray-700"
                title="More" data-testid="lp-more"
              >
                <DotsThreeVertical size={20} weight="bold" />
              </button>
            </div>
            {/* Overflow menu */}
            {contextMoreOpen && (
              <div
                className="fixed bg-white rounded-lg shadow-2xl border border-gray-200 z-50 py-1 min-w-[180px]"
                style={{ top: barTop + 48, left: Math.min(barLeft + 180, window.innerWidth - 200) }}
                data-testid="lp-more-menu"
              >
                {/* Point 7 — "Forward to Group" merged into the unified Forward
                    modal (long-press Forward now covers chats AND groups). */}
                {/* Point 1 (New Points) — one-on-one pin feature removed. */}
                {/* Session-49: Admin-only "Edit Entry" for completed deposit /
                    withdraw notices. Opens the shared EditEntryModal which
                    hits PATCH /deposit-entries|/withdraw-entries — the same
                    endpoint the group-chat "edited" badge uses. Sockets emit
                    `message_updated` so both this chat and the linked group
                    refresh instantly. */}
                {['deposit_notice', 'coin_cut_notice', 'withdraw_done_notice'].includes(msg?.meta?.kind) && msg?.meta?.entry_id && (
                  <button
                    onClick={() => {
                      const kind = msg.meta.kind;
                      const entryType = kind === 'deposit_notice' ? 'deposit' : 'withdraw';
                      setEditEntry({ type: entryType, entryId: msg.meta.entry_id });
                      setLongPressMenu(null);
                      setContextMoreOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-purple-600 text-sm"
                    data-testid="edit-entry-menu-btn"
                  >
                    <Copy size={16} /> Edit Entry{msg?.meta?.edit_count ? ` · ${msg.meta.edit_count} edited` : ''}
                  </button>
                )}
                {/* Session-49: "View Edit Previews" — only surfaces AFTER an
                    entry has at least one edit. Opens the same EditEntryModal
                    which auto-shows the history overlay. */}
                {['deposit_notice', 'coin_cut_notice', 'withdraw_done_notice'].includes(msg?.meta?.kind)
                  && msg?.meta?.entry_id
                  && Number(msg?.meta?.edit_count || 0) > 0 && (
                  <button
                    onClick={() => {
                      const entryType = msg.meta.kind === 'deposit_notice' ? 'deposit' : 'withdraw';
                      setEditEntry({ type: entryType, entryId: msg.meta.entry_id, openHistory: true });
                      setLongPressMenu(null);
                      setContextMoreOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-slate-700 text-sm"
                    data-testid="edit-previews-menu-btn"
                  >
                    <Copy size={16} /> View Edit Previews ({msg.meta.edit_count})
                  </button>
                )}
                {/* Delete is BLOCKED for automation notices — cancellation
                    must happen from the withdraw/deposit group flow so the
                    summary pending/paid/cancelled status stays in sync. */}
                {!['deposit_notice', 'coin_cut_notice', 'withdraw_done_notice'].includes(msg?.meta?.kind) && (
                  <button
                    onClick={() => { handleDeleteClick(longPressMenu.messageId); setContextMoreOpen(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-red-600 text-sm"
                  >
                    <Trash size={16} /> Delete
                  </button>
                )}
              </div>
            )}
          </>
        );
      })()}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Message</h3>
            <p className="text-gray-600 mb-6">Choose delete option:</p>
            <div className="space-y-2">
              <button
                onClick={() => handleDeleteMessage(false)}
                className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-left"
              >
                Delete for Me
              </button>
              <button
                onClick={() => handleDeleteMessage(true)}
                className="w-full px-4 py-3 bg-red-100 hover:bg-red-200 rounded-lg text-left text-red-600"
              >
                Delete for Everyone
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setMessageToDelete(null);
                }}
                className="w-full px-4 py-3 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Forward Modal - unified (chats + groups), Point 7 */}
      <MultiSelectForwardModal
        open={showForwardModal}
        chats={availableChats}
        groups={availableGroups}
        accentColor="#7c3aed"
        onClose={() => {
          setShowForwardModal(false);
          setMessageToForward(null);
        }}
        onForward={forwardSelected}
      />

      {/* MediaPicker */}
      {showMediaPicker && (
        <MediaPicker
          onMediaSelected={handleMediaSelected}
          onClose={() => { setShowMediaPicker(false); setPastedFile(null); }}
          userRole="admin"
          onOpenSavedTexts={() => setShowSavedTextsPanel(true)}
          onOpenSavedPhotos={() => setShowSavedPhotosPanel(true)}
          initialFile={pastedFile}
        />
      )}

      {/* Edit deposit/withdraw entry modal */}
      {editEntry && (
        <EditEntryModal
          open={!!editEntry}
          type={editEntry.type}
          entryId={editEntry.entryId}
          actorId={user.id}
          onClose={() => setEditEntry(null)}
          showPaidFields={editEntry.type === 'withdraw'}
          openHistoryOnMount={!!editEntry.openHistory}
          onEdited={() => { /* socket `message_updated` will refresh the bubble text/meta */ }}
        />
      )}

      {/* Clean reason-prompt modal (replaces window.prompt) */}
      <ReasonPromptModal
        open={!!reasonPrompt}
        title={reasonPrompt?.title || 'Reason'}
        helperText={reasonPrompt?.helper || ''}
        placeholder={reasonPrompt?.placeholder || ''}
        confirmLabel={reasonPrompt?.confirmLabel || 'Submit'}
        onCancel={() => setReasonPrompt(null)}
        onConfirm={(v) => reasonPrompt?.onConfirm?.(v)}
      />

      {/* Saved Texts Panel */}
      {showSavedTextsPanel && (
        <SavedTextsPanel
          userId={user.id}
          userRole="admin"
          onTextSelect={(text) => { sendMessage(null, text); setShowSavedTextsPanel(false); }}
          onClose={() => setShowSavedTextsPanel(false)}
        />
      )}

      {/* Saved Photos Panel */}
      {showSavedPhotosPanel && (
        <SavedPhotosPanel
          userId={user.id}
          onPhotoSelect={(media) => { handleMediaSelected(media); setShowSavedPhotosPanel(false); }}
          onClose={() => setShowSavedPhotosPanel(false)}
          onActionSticker={async ({ type, amount }) => {
            try {
              const resp = await axios.post(`${API}/chat/send-message`, {
                  room_id: roomId,
                  sender_id: user.id,
                  text: null,
                  action_sticker: { type, amount }
                });
              setMessages(prev => prev.some(m => m.id === resp.data.id) ? prev : [...prev, resp.data]);
              if (type === 'coin_cut') {
                axios.post(`${API}/coin-cut/broadcast`, {
                    actor_id: user.id,
                    customer_id: customer?.id,
                    amount,
                    room_id: roomId
                  }).catch(err => console.error('Coin cut broadcast failed (non-network):', err));
              }
            } catch (err) {
              console.error('Failed to send action sticker:', err);
            }
            setShowSavedPhotosPanel(false);
          }}
        />
      )}

      {/* Media Viewer - WhatsApp Style */}
      {showImagePreview && previewImageUrl && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col" data-testid="media-viewer">
          <div className="flex items-center justify-between p-4 text-white">
            <button onClick={closeImagePreview} className="p-2 hover:bg-white/10 rounded-full">
              <X size={24} weight="bold" />
            </button>
            <span className="text-sm font-medium">
              {allMediaUrls.length > 1 ? `${currentMediaIdx + 1} / ${allMediaUrls.length}` : 'Photo'}
            </span>
            <div className="w-10" />
          </div>
          <div className="flex-1 flex items-center justify-center px-4 relative">
            {allMediaUrls.length > 1 && currentMediaIdx > 0 && (
              <button onClick={() => { const i = currentMediaIdx - 1; setCurrentMediaIdx(i); setPreviewImageUrl(allMediaUrls[i]); }}
                className="absolute left-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white z-10">
                <ArrowLeft size={20} weight="bold" />
              </button>
            )}
            <div className="w-full h-full max-h-[75vh] flex-1">
              <ZoomableImage src={previewImageUrl} alt="Preview" />
            </div>
            {allMediaUrls.length > 1 && currentMediaIdx < allMediaUrls.length - 1 && (
              <button onClick={() => { const i = currentMediaIdx + 1; setCurrentMediaIdx(i); setPreviewImageUrl(allMediaUrls[i]); }}
                className="absolute right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white z-10">
                <CaretDown size={20} weight="bold" className="rotate-[-90deg]" />
              </button>
            )}
          </div>
          <div className="flex items-center justify-around p-4 border-t border-white/10">
            <button onClick={() => { if(navigator.share) navigator.share({url: previewImageUrl}).catch(()=>{}); else { navigator.clipboard.writeText(previewImageUrl); toast('Link copied'); } }}
              className="flex flex-col items-center gap-1 text-white/70 hover:text-white">
              <ShareFat size={22} /><span className="text-[10px]">Forward</span>
            </button>
            <button onClick={closeImagePreview} className="flex flex-col items-center gap-1 text-white/70 hover:text-white">
              <ArrowLeft size={22} /><span className="text-[10px]">Back</span>
            </button>
            <a href={previewImageUrl} download="photo.jpg" target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-1 text-white/70 hover:text-white">
              <CaretDown size={22} weight="bold" /><span className="text-[10px]">Download</span>
            </a>
          </div>
        </div>
      )}

      {/* Register Customer Modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Register Customer</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Customer name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                <input
                  type="tel"
                  value={registerForm.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 10) setRegisterForm({...registerForm, phone: val});
                  }}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="10-digit mobile number"
                  maxLength={10}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="text"
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Default: Abcd1234"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setShowRegisterModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleRegisterCustomer}
                disabled={registering}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                {registering ? 'Registering...' : 'Register'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {showEditModal && editClientData && (
        <EditClientModal
          client={editClientData}
          isRegistered={true}
          userId={user?.id}
          onClose={() => {
            setShowEditModal(false);
            setEditClientData(null);
            const loadUpdated = async () => {
              try {
                const roomResponse = await axios.get(`${API}/chat/room/${roomId}`);
                const room = roomResponse.data;
                if (room) {
                  setCustomer({
                    id: room.customer_id,
                    name: room.customer_name,
                    phone: room.customer_phone,
                    profile_pic: room.customer_profile_pic,
                    is_guest: room.customer_is_guest || false,
                    role: room.customer_role || 'customer',
                    mb_setup: room.mb_setup || null,
                    chat_type: room.chat_type || null
                  });
                }
              } catch (e) { console.error(e); }
            };
            loadUpdated();
          }}
        />
      )}

      {/* Point 7 — separate "Forward to Group" modal removed; unified into
          MultiSelectForwardModal above. */}


      {/* Audio Call Screen */}
      {activeCall && (
        <AudioCallScreen
          socket={socket}
          callId={activeCall.callId}
          callerId={activeCall.callerId}
          calleeId={activeCall.calleeId}
          callerName={activeCall.callerName}
          calleeName={activeCall.calleeName}
          callerProfilePic={activeCall.callerProfilePic}
          calleeProfilePic={activeCall.calleeProfilePic}
          isIncoming={activeCall.isIncoming}
          currentUserId={user.id}
          currentUserRole="admin"
          isConference={activeCall.isConference || false}
          onClose={() => setActiveCall(null)}
          API={API}
        />
      )}

      {/* Incoming Call */}
      {incomingCall && !activeCall && (
        <div className="fixed inset-0 z-[90] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#1a3a2a] rounded-2xl p-8 max-w-xs w-full text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00a884] to-[#008069] mx-auto mb-4 flex items-center justify-center">
              <span className="text-3xl font-bold text-white">{(incomingCall.callerName || 'U')[0]?.toUpperCase()}</span>
            </div>
            <h3 className="text-xl font-bold text-white mb-1">{incomingCall.callerName}</h3>
            <p className="text-white/50 text-sm mb-6">
              {incomingCall.isConference 
                ? `Conference call (${incomingCall.participantNames?.join(', ')})` 
                : 'Incoming audio call...'}
            </p>
            <div className="flex justify-around">
              <button onClick={() => { socket.emit('call_reject', { call_id: incomingCall.callId }); setIncomingCall(null); }}
                className="w-14 h-14 rounded-full bg-red-500 flex items-center justify-center">
                <Phone size={24} weight="fill" className="text-white rotate-[135deg]" />
              </button>
              <button onClick={() => {
                setActiveCall({ callId: incomingCall.callId, callerId: incomingCall.callerId, calleeId: user.id,
                  callerName: incomingCall.callerName, calleeName: user.username, isIncoming: true,
                  isConference: incomingCall.isConference || false });
                setIncomingCall(null);
              }} className="w-14 h-14 rounded-full bg-[#00a884] flex items-center justify-center">
                <Phone size={24} weight="fill" className="text-white" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin-only: preview the ORIGINAL deleted-message content by tapping
          the placeholder. Body remains hidden from all other roles. */}
      {deletedPreview && (
        <div
          className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4"
          onClick={() => setDeletedPreview(null)}
          data-testid="deleted-preview-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full max-h-[80vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between bg-slate-100 px-4 py-3 border-b">
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-slate-800">🕵️ Deleted Message Preview</h3>
                <p className="text-[10px] text-slate-500">
                  Deleted by {deletedPreview.deleted_by_username || 'user'} · {(deletedPreview.deleted_by_role || '').replace('_', ' ')}
                  {deletedPreview.deleted_at ? ` · ${String(deletedPreview.deleted_at).slice(0, 16).replace('T', ' ')}` : ''}
                </p>
              </div>
              <button
                onClick={() => setDeletedPreview(null)}
                className="p-1.5 hover:bg-slate-200 rounded-full"
                data-testid="deleted-preview-close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh] space-y-3">
              {deletedPreview.original_image_url && (
                <img
                  src={buildMediaUrl(deletedPreview.original_image_url)}
                  alt="Original"
                  className={deletedPreview.original_is_sticker
                    ? "w-[140px] h-auto object-contain mx-auto bg-transparent"
                    : "max-w-full max-h-[45vh] rounded-lg object-contain mx-auto"}
                />
              )}
              {deletedPreview.original_voice_url && (
                <audio src={buildMediaUrl(deletedPreview.original_voice_url)} controls className="w-full" />
              )}
              {deletedPreview.original_document_url && (
                <a
                  href={buildMediaUrl(deletedPreview.original_document_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="block px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 hover:bg-slate-100"
                >
                  📄 {deletedPreview.original_document_name || 'Document'}
                </a>
              )}
              {deletedPreview.original_text ? (
                <p className="text-[15px] text-slate-800 whitespace-pre-line leading-relaxed" data-testid="deleted-preview-text">
                  {deletedPreview.original_text}
                </p>
              ) : !deletedPreview.original_image_url && !deletedPreview.original_voice_url && !deletedPreview.original_document_url ? (
                <p className="text-sm text-slate-400 italic text-center">Original content wasn't captured (older message).</p>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Session-42: Admin-side Create Request modal */}
      {showSRCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-end sm:items-center justify-center p-2"
             onClick={() => { if (!srBusy) { setShowSRCreateModal(false); setSrAmount(''); setSrReference(''); } }}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md p-4"
               data-testid="admin-sr-create-modal">
            <h3 className="font-bold text-lg mb-3">
              🎫 Create {customer?.chat_type === 'deposit' ? 'Deposit' : customer?.chat_type === 'withdrawal' ? 'Withdraw' : 'Support'} Request for {customer?.name || customer?.username}
            </h3>
            {customer?.chat_type !== 'support' && (
              <div className="mb-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Amount (₹)</label>
                <input type="number" value={srAmount}
                  onChange={(e) => setSrAmount(e.target.value)}
                  placeholder="e.g. 5000"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  data-testid="admin-sr-amount-input" />
              </div>
            )}
            {/* P23 — Deposit: UTR (required) + optional received-in/note → Create & Approve */}
            {customer?.chat_type === 'deposit' && (
              <>
                <div className="mb-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">UTR Number (required)</label>
                  <input type="text" value={srUtr} onChange={(e) => setSrUtr(e.target.value)}
                    placeholder="6–22 letters/digits"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
                    data-testid="admin-sr-utr-input" />
                </div>
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Received In (optional)</label>
                  <input type="text" value={srReceivedIn} onChange={(e) => setSrReceivedIn(e.target.value)}
                    placeholder="e.g. HDFC ****1234 / UPI"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                    data-testid="admin-sr-receivedin-input" />
                </div>
              </>
            )}
            {/* P24 — Withdraw: prefilled editable bank details + UTR → Approve & Coin Cut */}
            {customer?.chat_type === 'withdrawal' && (
              <div className="mb-3 space-y-2 border border-slate-200 rounded-lg p-2.5 bg-slate-50">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold text-slate-500">Bank details (prefilled from client — editable)</p>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const raw = await navigator.clipboard.readText();
                        if (!raw || !raw.trim()) { toast.error('Clipboard is empty'); return; }
                        const parsed = parseBankFromPaste(raw);
                        const keys = Object.keys(parsed);
                        if (keys.length === 0) { toast.error('No bank details found in clipboard'); return; }
                        setSrBank(b => {
                          const next = { ...b };
                          for (const k of keys) if (parsed[k]) next[k] = parsed[k];
                          return next;
                        });
                        toast.success(`Filled ${keys.length} field${keys.length === 1 ? '' : 's'} from clipboard`);
                      } catch (err) {
                        toast.error('Clipboard access denied — allow permission and try again');
                      }
                    }}
                    className="px-2 py-0.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold uppercase tracking-wide shadow-sm flex items-center gap-1 flex-shrink-0"
                    data-testid="admin-sr-bank-paste-btn"
                    title="Paste a copied withdrawal form — auto-fills the fields"
                  >
                    <span>📋</span> Paste
                  </button>
                </div>
                {srBankAccounts.length > 1 && (
                  <select
                    value={srBankPickId}
                    onChange={(e) => {
                      const acc = srBankAccounts.find(a => a.id === e.target.value);
                      setSrBankPickId(e.target.value);
                      if (acc) {
                        const v = acc.value;
                        setSrBank(b => ({ ...b, account_holder_name: v.account_holder_name || '', bank_account_number: v.bank_account_number || '', ifsc_code: v.ifsc_code || '', bank_name: v.bank_name || '' }));
                      }
                    }}
                    className="w-full px-3 py-2 border border-blue-300 rounded-md text-sm bg-white font-medium"
                    data-testid="admin-sr-bank-picker"
                  >
                    {srBankAccounts.map(a => (
                      <option key={a.id} value={a.id}>
                        {(a.value.account_holder_name || 'Account')} · {a.value.bank_name || ''} ····{String(a.value.bank_account_number || '').slice(-4)}{a.is_active ? ' (active)' : ''}
                      </option>
                    ))}
                  </select>
                )}
                <input type="text" value={srBank.account_holder_name} onChange={(e) => setSrBank(b => ({ ...b, account_holder_name: e.target.value }))} placeholder="Account holder name" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="admin-sr-bank-name-input" />
                <input type="text" value={srBank.bank_account_number} onChange={(e) => setSrBank(b => ({ ...b, bank_account_number: e.target.value }))} placeholder="Account number" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono" data-testid="admin-sr-bank-acc-input" />
                <div className="flex gap-2">
                  <input type="text" value={srBank.ifsc_code} onChange={(e) => setSrBank(b => ({ ...b, ifsc_code: e.target.value.toUpperCase() }))} placeholder="IFSC" className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono uppercase" data-testid="admin-sr-bank-ifsc-input" />
                  <input type="text" value={srBank.bank_name} onChange={(e) => setSrBank(b => ({ ...b, bank_name: e.target.value }))} placeholder="Bank name" className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="admin-sr-bank-bankname-input" />
                </div>
                <input type="text" value={srBank.upi_id} onChange={(e) => setSrBank(b => ({ ...b, upi_id: e.target.value }))} placeholder="UPI ID (optional)" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="admin-sr-bank-upi-input" />
              </div>
            )}
            <div className="mb-3">
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                {customer?.chat_type === 'support' ? 'Query / Message (required)' : 'Reference (optional)'}
              </label>
              <textarea value={srReference} onChange={(e) => setSrReference(e.target.value)}
                rows={2} placeholder="Note or reference"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                data-testid="admin-sr-reference-input" />
            </div>
            <p className="text-[11px] text-slate-500 mb-3">
              "Requested by: admin" chat card mein show hoga.
            </p>
            <div className="flex gap-2 justify-end">
              <button disabled={srBusy} onClick={() => { setShowSRCreateModal(false); setSrAmount(''); setSrReference(''); setSrUtr(''); setSrReceivedIn(''); setSrDesc(''); }}
                className="px-3 py-1.5 text-sm rounded border border-slate-300">Cancel</button>
              <button disabled={srBusy}
                onClick={async () => {
                  const ct = customer?.chat_type;
                  if (ct === 'support' && !srReference.trim()) { toast.error('Enter the query'); return; }
                  if (ct !== 'support' && !srAmount) { toast.error('Enter the amount'); return; }
                  if (ct === 'deposit') {
                    const utr = srUtr.trim();
                    if (!/^[A-Za-z0-9]{6,22}$/.test(utr)) { toast.error('Enter a valid UTR (6–22 chars)'); return; }
                  }
                  setSrBusy(true);
                  try {
                    const backendType = ct === 'withdrawal' ? 'withdraw' : ct;
                    // Step 1 — create the request (sends the ticket-created message)
                    await axios.post(`${API}/service-requests/create`, {
                        type: backendType,
                        room_id: roomId,
                        actor_id: user.id,
                        on_behalf_customer_id: customer.id,
                        amount: srAmount ? Number(srAmount) : undefined,
                        reference: srReference.trim() || undefined,
                      });
                    // Step 2 — immediately approve (sends the done / coins-cut message)
                    if (ct === 'deposit') {
                      await axios.post(`${API}/deposit-done/notify`, {
                        actor_id: user.id, customer_id: customer.id, amount: String(srAmount),
                        room_id: roomId, utr_number: srUtr.trim(), received_in: srReceivedIn.trim(),
                        received_from: '', description: srDesc.trim(), bank_account: srReceivedIn.trim(),
                      });
                      toast.success('✅ Request created & deposit approved');
                    } else if (ct === 'withdrawal') {
                      await axios.post(`${API}/coin-cut/broadcast`, {
                        actor_id: user.id, customer_id: customer.id, amount: String(srAmount),
                        room_id: roomId, reference: srReference.trim() || null,
                        bank_entry_id: srBankPickId || undefined,
                        bank_override: {
                          account_holder_name: srBank.account_holder_name.trim(),
                          bank_account_number: srBank.bank_account_number.trim(),
                          ifsc_code: srBank.ifsc_code.trim(),
                          bank_name: srBank.bank_name.trim(),
                          upi_id: srBank.upi_id.trim(),
                        },
                      });
                      toast.success('✅ Request created & coin cut done');
                    } else {
                      toast.success('✅ Request created');
                    }
                    setShowSRCreateModal(false);
                    setSrAmount(''); setSrReference(''); setSrUtr(''); setSrReceivedIn(''); setSrDesc('');
                    loadMessages();
                    setTimeout(() => loadMessages(), 800);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Failed');
                  } finally { setSrBusy(false); }
                }}
                className="px-4 py-1.5 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                data-testid="admin-sr-create-submit">
                {srBusy ? 'Processing…' : customer?.chat_type === 'deposit' ? 'Create & Approve' : customer?.chat_type === 'withdrawal' ? 'Approve & Coin Cut' : 'Create Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session-42: Admin-side Cancel Request modal with per-request reason */}
      {showSRCancelModal && (
        <DraggableModal
          open={showSRCancelModal}
          onClose={() => { if (!srBusy) { setShowSRCancelModal(false); setSrReasonForId(null); setSrReasonText(''); } }}
          testId="admin-sr-cancel-modal"
          title="❌ Cancel Open Requests"
        >
          <div>
            <p className="text-[11px] text-slate-500 mb-2">Select a request to cancel for <b>{customer?.name || customer?.username}</b></p>
            <div className="space-y-2 mb-3">
              {srCancelList.map(req => (
                <div key={req.id} className="border border-slate-200 rounded-md p-2.5 bg-slate-50">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="font-bold text-slate-800 text-sm">{req.request_no}</div>
                    <div className="text-[11px] text-slate-500">
                      {req.amount ? `₹${req.amount}` : ''} {req.reference ? `· ${req.reference}` : ''}
                    </div>
                  </div>
                  {srReasonForId === req.id ? (
                    <>
                      <textarea value={srReasonText} onChange={(e) => setSrReasonText(e.target.value)}
                        placeholder="Reason" rows={2} autoFocus
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs resize-none"
                        data-testid={`admin-sr-cancel-reason-${req.request_no}`} />
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => { setSrReasonForId(null); setSrReasonText(''); }}
                          disabled={srBusy}
                          className="flex-1 py-1.5 border border-slate-300 rounded text-xs">Back</button>
                        <button disabled={srBusy || !srReasonText.trim()}
                          onClick={async () => {
                            setSrBusy(true);
                            try {
                              await axios.post(`${API}/service-requests/cancel`, {
                                  request_id: req.id, actor_id: user.id, reason: srReasonText.trim(),
                                });
                              setSrCancelList(prev => prev.filter(r => r.id !== req.id));
                              setSrReasonForId(null); setSrReasonText('');
                              toast.success(`❌ ${req.request_no} cancelled`);
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Failed');
                            } finally { setSrBusy(false); }
                          }}
                          className="flex-1 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-bold disabled:opacity-50"
                          data-testid={`admin-sr-cancel-submit-${req.request_no}`}
                        >{srBusy ? 'Cancelling…' : 'Confirm Cancel'}</button>
                      </div>
                    </>
                  ) : (
                    <button disabled={srBusy}
                      onClick={() => { setSrReasonForId(req.id); setSrReasonText(''); }}
                      className="w-full py-1 rounded bg-red-50 hover:bg-red-100 text-red-700 text-xs font-semibold border border-red-200"
                    >Cancel this request →</button>
                  )}
                </div>
              ))}
              {srCancelList.length === 0 && (
                <div className="text-center text-slate-400 text-sm py-4">All cancelled.</div>
              )}
            </div>
            <div className="flex justify-end">
              <button onClick={() => { setShowSRCancelModal(false); setSrReasonForId(null); setSrReasonText(''); }}
                className="px-3 py-1.5 text-sm rounded border border-slate-300">Close</button>
            </div>
          </div>
        </DraggableModal>
      )}
      {/* Session-48j — Mark-as-Solved note modal */}
      <MarkSolvedNoteModal
        open={markSolvedOpen}
        busy={markSolvedBusy}
        onClose={() => setMarkSolvedOpen(false)}
        onConfirm={doMarkSolved}
      />
    </div>
  );
};

export default AdminChat;
